/* Marva Design System — components.
   Vendored from the design system (components/src/index.jsx); see ./README.md before editing.
   Every screen imports from here (`@/ds` / '../ds'); no screen builds its own styled control. */
import * as React from 'react';
import * as ReactDOM from 'react-dom';
const { useEffect, useRef, useId, useCallback } = React;

const cx = (...a) => a.filter(Boolean).join(' ');
/* Path data: one string, an array of strings, or one string with `|` between paths. */
const pathsOf = (d) => [].concat(d == null ? [] : d).flatMap((p) => String(p).split('|')).filter(Boolean);
const Svg = ({ d, size, sw = 1.8, className }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={sw}
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" className={className || undefined}>
    {pathsOf(d).map((p, i) => <path key={i} d={p} />)}
  </svg>
);
const ICONS = {
  x: 'M6 6l12 12M18 6L6 18',
  check: 'M5 12.5l4.2 4.2L19 7',
  alert: ['M12 8v5', 'M12 16.5v.01', 'M10.3 3.9L2.6 17.2A2 2 0 0 0 4.3 20h15.4a2 2 0 0 0 1.7-2.8L13.7 3.9a2 2 0 0 0-3.4 0z'],
  sun: ['M3 17.5h18', 'M7 17.5a5 5 0 0 1 10 0', 'M12 4v2.2', 'M5 9.5l1.5 1.5', 'M19 9.5l-1.5 1.5'],
  moon: 'M21 12.8A8.5 8.5 0 1 1 11.2 3a6.6 6.6 0 0 0 9.8 9.8z',
};

/* ---------------- Icon ----------------
   The one line-icon renderer: a 24px stroked glyph on currentColor, decorative (the control
   that holds it carries the name). `name` picks a design-system glyph; `d` takes path data
   (a string, an array, or `|`-separated) for an app glyph. Sized by the component that holds
   it (`.mv-navitem-ic svg`, `.mv-ibtn svg`, `.mv-menu-ic svg`…) unless `size` is given. */
function Icon({ name, d, size, stroke = 1.8, className = '' }) {
  const paths = d != null ? d : ICONS[name];
  if (paths == null) return null;
  return <Svg d={paths} size={size} sw={stroke} className={className} />;
}

/* ---------------- Orb ---------------- */
function Orb({ size = 48, float = false, responsive = false, label, className = '', style = {}, ...rest }) {
  const a11y = label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true };
  return <span className={cx('mv-orb', float && 'mv-orb--float', responsive && 'mv-orb--responsive', className)}
    style={{ width: size, height: size, ...style }} {...a11y} {...rest} />;
}

/* ---------------- Eyebrow ---------------- */
function Eyebrow({ children, tone = 'gold', size = 'default', as: Tag = 'p', className = '', style = {}, ...rest }) {
  return <Tag className={cx('mv-eyebrow', size === 'page' && 'mv-eyebrow--page', tone === 'muted' && 'mv-eyebrow--muted', className)} style={style} {...rest}>{children}</Tag>;
}

/* ---------------- Button ---------------- */
const Button = React.forwardRef(function Button({
  children, variant = 'gold', size = 'md', icon, iconRight, iconOnly = false, label,
  busy = false, busyLabel, disabled = false, block = false, wrap = false,
  type = 'button', href, onClick, className = '', style = {}, ...rest
}, ref) {
  if (iconOnly && !label && typeof console !== 'undefined') console.warn('Button: iconOnly needs `label` (its accessible name).');
  const inert = disabled || busy;
  // `link`: an inline text action inside copy or a row ("Show all", "Undo") — `.mv-linkbtn`.
  // No size, no icon slot, no pill; everything else (busy, disabled, href) is the same Button.
  const cls = variant === 'link' ? cx('mv-linkbtn', className)
    : cx('mv-btn', `mv-btn--${variant}`, size !== 'md' && `mv-btn--${size}`, iconOnly && 'mv-btn--icon',
      block && 'mv-btn--block', wrap && 'mv-btn--wrap', className);
  const lead = busy ? <span className="mv-spin" aria-hidden="true" /> : icon;
  const content = iconOnly
    ? <>{busy ? lead : (icon || children)}</>
    // While busy with a busyLabel, the live region below is the name; the visible copy is hidden
    // from assistive tech so the button is not announced as "Saving… Saving…".
    : <>{lead}<span aria-hidden={busy && busyLabel ? true : undefined}>{busy && busyLabel ? busyLabel : children}</span>{!busy && iconRight}</>;
  const common = {
    className: cls, style,
    'aria-label': iconOnly ? label : rest['aria-label'],
    title: iconOnly ? (rest.title || label) : rest.title,
    'aria-busy': busy || undefined,
  };
  const handle = (e) => { if (inert) { e.preventDefault(); return; } onClick && onClick(e); };
  if (href) {
    return <a ref={ref} {...rest} {...common} href={inert ? undefined : href} role={inert ? 'link' : undefined}
      aria-disabled={inert || undefined} onClick={handle}>{content}</a>;
  }
  // busy keeps focus (aria-disabled) so the keyboard user is not dropped; disabled removes it.
  return <button ref={ref} {...rest} {...common} type={type} disabled={disabled && !busy}
    aria-disabled={busy || undefined} onClick={handle}>{content}{busy && <span className="mv-sr" aria-live="polite">{busyLabel || 'Working'}</span>}</button>;
});

/* ---------------- Card ---------------- */
function Card({
  children, tone = 'plain', padding = 'md', interactive = false, selected, selectable = false,
  disabled = false, fit = false, centered = false, as, href, onClick, className = '', style = {}, ...rest
}) {
  const cls = cx('mv-card', tone !== 'plain' && `mv-card--${tone}`, padding !== 'md' && `mv-card--pad-${padding}`,
    fit && 'mv-card--fit', centered && 'mv-card--center',
    interactive && 'mv-card--interactive', !interactive && selected && 'mv-card--selected', className);
  if (interactive) {
    if (href) return <a {...rest} className={cls} style={style} href={disabled ? undefined : href} aria-disabled={disabled || undefined}>{children}</a>;
    return <button {...rest} type="button" className={cls} style={style} disabled={disabled} onClick={onClick}
      aria-pressed={selectable ? !!selected : undefined}>{children}</button>;
  }
  const Tag = as || 'div';
  return <Tag {...rest} className={cls} style={style}>{children}</Tag>;
}
/* `inline`: every element is a <span>, for a header inside phrasing content — the label of
   an interactive Card (a <button>/<a>), where a block element is not allowed. */
function CardHeader({ title, description, icon, aside, titleAs, inline = false, className = '', children }) {
  const B = inline ? 'span' : 'div';
  const T = titleAs || (inline ? 'span' : 'h3');
  return (
    <B className={cx('mv-card-head', className)}>
      {icon}
      <B className="mv-card-titles">
        {title && <T className="mv-card-title">{title}</T>}
        {description && <B className="mv-card-desc">{description}</B>}
        {children}
      </B>
      {aside && <B className="mv-card-aside">{aside}</B>}
    </B>
  );
}
function CardBody({ children, className = '' }) { return <div className={cx('mv-card-body', className)}>{children}</div>; }
function CardFooter({ children, className = '' }) { return <div className={cx('mv-card-foot', className)}>{children}</div>; }
function Stat({ value, label, tone = 'plain' }) {
  return <div className="mv-stat"><div className={cx('n', tone === 'gold' && 'gold')}>{value}</div><div className="l">{label}</div></div>;
}

/* ---------------- Chip ---------------- */
function Chip({
  children, tone = 'neutral', icon, dot = false, kind = 'status', selected = false, onToggle,
  onRemove, removeLabel, wrap = false, disabled = false, title, count, className = '', style = {}, ...rest
}) {
  const text = typeof children === 'string' ? children : undefined;
  // `count`: a number after the label, in mono (a filter's tally) — inside the label so the
  // accessible name reads "Everything 21".
  const label = <span className="mv-chip-label">{children}{count != null && count !== '' && <>{' '}<span className="mv-chip-count">{count}</span></>}</span>;
  if (kind === 'select') {
    return <button {...rest} type="button" className={cx('mv-chip', 'mv-chip--select', wrap && 'mv-chip--wrap', className)} style={style}
      aria-pressed={!!selected} disabled={disabled} title={title || (wrap ? undefined : text)} onClick={onToggle}>{icon}{label}</button>;
  }
  if (kind === 'remove') {
    return (
      <span {...rest} className={cx('mv-chip', 'mv-chip--remove', wrap && 'mv-chip--wrap', className)} style={style} title={title || (wrap ? undefined : text)}>
        {icon}{label}
        <button type="button" className="mv-chip-x" aria-label={removeLabel || `Remove ${text || ''}`.trim()} onClick={onRemove} disabled={disabled}>
          <Svg d={ICONS.x} sw={2} />
        </button>
      </span>
    );
  }
  return <span {...rest} className={cx('mv-chip', tone !== 'neutral' && `mv-chip--${tone}`, wrap && 'mv-chip--wrap', className)} style={style}
    title={title || (wrap ? undefined : text)}>{dot && <span className="mv-chip-dot" aria-hidden="true" />}{icon}{label}</span>;
}
function ChipGroup({ children, label, className = '' }) {
  return <div className={cx('mv-chips', className)} role={label ? 'group' : undefined} aria-label={label}>{children}</div>;
}

/* ---------------- Field ---------------- */
function Field({
  label, hint, error, success, required = false, optional = false, disabled = false, readOnly = false,
  as = 'input', options, prefix, suffix, id, rows = 3, className = '', style = {}, controlRef, controlProps, children,
  revealable = false, type = 'text', ...rest
}) {
  const auto = useId();
  const [shown, setShown] = React.useState(false);
  const fid = id || `mv-f${auto.replace(/:/g, '')}`;
  const hintId = hint ? `${fid}-hint` : undefined;
  const msgId = error || success ? `${fid}-msg` : undefined;
  const describedBy = [msgId, hintId].filter(Boolean).join(' ') || undefined;
  const state = error ? 'error' : success ? 'success' : null;
  const common = {
    id: fid, className: 'mv-input', disabled, readOnly: as !== 'select' ? readOnly : undefined, required,
    'aria-required': required || undefined, 'aria-invalid': error ? true : undefined, 'aria-describedby': describedBy, ref: controlRef, ...rest,
  };
  let control;
  if (as === 'textarea') control = <textarea rows={rows} {...common} />;
  else if (as === 'select') control = <select {...common} disabled={disabled || readOnly}>{children || (options || []).map((o) => {
    const v = typeof o === 'string' ? { value: o, label: o } : o; return <option key={v.value} value={v.value}>{v.label}</option>;
  })}</select>;
  else control = <input {...common} type={type === 'password' && shown ? 'text' : type}
    spellCheck={['password', 'email', 'tel', 'url', 'number'].includes(type) ? false : rest.spellCheck}
    autoCapitalize={['password', 'email', 'url'].includes(type) ? 'none' : rest.autoCapitalize}
    onWheel={type === 'number' && !rest.onWheel ? (e) => e.currentTarget.blur() : rest.onWheel} />;
  return (
    <div className={cx('mv-field', state && `mv-field--${state}`, disabled && 'mv-field--disabled', readOnly && 'mv-field--readonly', className)} style={style}>
      {label && <label className="mv-label" htmlFor={fid}>{label}{optional && !required && <span className="opt">(optional)</span>}</label>}
      <div {...controlProps} className={cx('mv-control', as === 'select' && 'mv-control--select')}>
        {prefix && <span className="mv-affix mv-affix--pre" aria-hidden="true">{prefix}</span>}
        {control}
        {suffix && <span className="mv-affix mv-affix--suf" aria-hidden="true">{suffix}</span>}
        {revealable && type === 'password' && <button type="button" className="mv-reveal" aria-pressed={shown} aria-controls={fid}
          disabled={disabled} onClick={() => setShown((v) => !v)}>{shown ? 'Hide' : 'Show'}<span className="mv-sr">{typeof label === 'string' ? ` ${label}` : ''}</span></button>}
      </div>
      {state && <div id={msgId} className={`mv-msg mv-msg--${state}`} role={error ? 'alert' : 'status'}>
        <Svg d={error ? ICONS.alert : ICONS.check} sw={2} /><span>{error || success}</span></div>}
      {hint && <div id={hintId} className="mv-hint">{hint}</div>}
    </div>
  );
}

/* ---------------- TrustPill ---------------- */
const TRUST = {
  shadow: { name: 'Shadow', desc: 'She drafts everything and sends nothing' },
  assisted: { name: 'Assisted', desc: 'She acts inside your bounds and brings you the rest' },
  autonomous: { name: 'Autonomous', desc: 'She acts on trust within your guardrails' },
};
function TrustPill({ level = 'assisted', label, onClick, href, compact = false, className = '', style = {}, ...rest }) {
  const t = TRUST[level] || { name: level, desc: '' };
  const text = label || t.name;
  const interactive = !!(onClick || href);
  const cls = cx('mv-trust', `mv-trust--${level}`, interactive && 'mv-trust--button', compact && 'mv-trust--compact', className);
  const inner = <><span className="dot" aria-hidden="true" />{compact ? <span className="mv-sr">{text}</span> : text}</>;
  const tip = t.desc + (interactive ? ' — open Authority to change it' : '');
  if (href) return <a {...rest} className={cls} style={style} href={href} title={tip} aria-label={`Trust level: ${text}. ${tip}`}>{inner}</a>;
  if (onClick) return <button {...rest} type="button" className={cls} style={style} onClick={onClick} title={tip} aria-label={`Trust level: ${text}. ${tip}`}>{inner}</button>;
  return <span {...rest} className={cls} style={style} title={tip}>{!compact && <span className="mv-sr">Trust level: </span>}{inner}</span>;
}

/* ---------------- ThemeToggle ---------------- */
const MODE = { auto: 'Auto', day: 'Daylight', night: 'After dark' };
const NEXT = { auto: 'day', day: 'night', night: 'auto' };
function ThemeToggle({ mode = 'auto', theme, onChange, variant = 'pill', showLabel = true, className = '', style = {}, ...rest }) {
  const resolved = theme || (mode === 'night' ? 'dark' : mode === 'day' ? 'light' : 'light');
  const next = NEXT[mode] || 'auto';
  const icon = resolved === 'dark' ? ICONS.moon : ICONS.sun;
  const name = `Theme: ${MODE[mode]}${mode === 'auto' ? ` (${resolved === 'dark' ? 'After Hours' : 'Daylight'} now)` : ''}. Switch to ${MODE[next]}`;
  return (
    <button {...rest} type="button" className={cx('mv-theme', variant === 'rail' && 'mv-theme--rail', variant === 'icon' && 'mv-theme--icon', className)}
      style={style} onClick={() => onChange && onChange(next)} aria-label={name} title={name}>
      <Svg d={icon} sw={1.7} />
      {variant !== 'icon' && showLabel && <span className="tl" aria-hidden="true">{MODE[mode]}</span>}
    </button>
  );
}

/* ---------------- Overlay plumbing: one Escape stack, focus trap, restore ---------------- */
const STACK = [];
let keyBound = false;
function onDocKey(e) {
  const top = STACK[STACK.length - 1];
  if (!top) return;
  if (e.key === 'Escape') {
    if (top.dismissible) { e.stopPropagation(); top.close(); }
    return;
  }
  if (e.key !== 'Tab' || !top.trap) return;
  const root = top.ref.current; if (!root) return;
  const f = root.querySelectorAll('a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])');
  if (!f.length) { e.preventDefault(); root.focus(); return; }
  // Focus can sit on something the list leaves out — the panel itself, or the drawer title
  // (`tabIndex=-1`, where focus lands on open, MAR-381). The browser's own Tab order from there
  // runs out of the panel whenever no control lies that way, so the trap asks the DOCUMENT
  // order, not list membership: Shift+Tab with no control before focus wraps to the last,
  // Tab with none after it wraps to the first. Focus outside the panel is pulled back in.
  const list = Array.from(f), a = document.activeElement;
  const inside = root.contains(a);
  const precedes = (el) => el !== a && (el.compareDocumentPosition(a) & Node.DOCUMENT_POSITION_FOLLOWING);
  const follows = (el) => el !== a && (el.compareDocumentPosition(a) & Node.DOCUMENT_POSITION_PRECEDING);
  if (e.shiftKey && (!inside || !list.some(precedes))) { e.preventDefault(); list[list.length - 1].focus(); }
  else if (!e.shiftKey && (!inside || !list.some(follows))) { e.preventDefault(); list[0].focus(); }
}
/* Page scroll lock while any overlay is open (D-14): the document stops scrolling,
   the scrollbar's width is kept so nothing shifts. Overlays scroll their own bodies. */
let saved = null;
function lockScroll() {
  if (saved || typeof document === 'undefined') return;
  const el = document.documentElement;
  const gap = window.innerWidth - el.clientWidth;
  saved = { overflow: el.style.overflow, pad: el.style.paddingRight };
  el.style.overflow = 'hidden';
  if (gap > 0) el.style.paddingRight = `${gap}px`;
}
function unlockScroll() {
  if (!saved) return;
  const el = document.documentElement;
  el.style.overflow = saved.overflow; el.style.paddingRight = saved.pad; saved = null;
}
/* A non-modal drawer (`trap: false`) sits BESIDE the Ask dock (overlays.md): Tab may leave it
   for the dock, but never for the page behind its scrim. While it is open everything else is
   made `inert` — walking from the dock (`[data-mv-beside-overlays]`) up to <body>, every
   sibling along the path that is not the overlay layer (nor another portalled `.mv-layer`:
   toasts, the overlay band). With no dock mounted, everything but the overlay layers.
   Drawers stack, so `inert` is reference-counted per element (INERT_HELD): set on the first
   hold, removed only when the last holder closes — drawer B over drawer A keeps the page
   inert after A closes. An element inert before any drawer held it is never touched. */
const INERT_HELD = new Map();
function holdInert(el, made) {
  const n = INERT_HELD.get(el) || 0;
  if (n === 0 && el.hasAttribute('inert')) return; // pre-existing: not ours to set or remove
  if (n === 0) el.setAttribute('inert', '');
  INERT_HELD.set(el, n + 1); made.push(el);
}
function releaseInert(el) {
  const n = INERT_HELD.get(el) || 0;
  if (n <= 1) { INERT_HELD.delete(el); el.removeAttribute('inert'); } else INERT_HELD.set(el, n - 1);
}
function inertBehind(panel) {
  if (typeof document === 'undefined' || !panel) return () => {};
  const layer = panel.closest('.mv-layer') || panel;
  const dock = document.querySelector('[data-mv-beside-overlays]');
  const start = dock && !layer.contains(dock) ? dock : null;
  const made = [];
  const skip = (el) => el === layer || el.contains(layer) || (el.parentElement === document.body && el.classList.contains('mv-layer'))
    || /^(SCRIPT|STYLE|TEMPLATE|LINK|META)$/.test(el.tagName);
  const inertSiblings = (node) => {
    const parent = node.parentElement; if (!parent) return;
    for (const el of parent.children) {
      if (el === node || skip(el)) continue;
      holdInert(el, made);
    }
  };
  if (start) {
    for (let node = start; node && node !== document.body && node.parentElement; node = node.parentElement) inertSiblings(node);
  } else {
    for (const el of document.body.children) {
      if (skip(el)) continue;
      holdInert(el, made);
    }
  }
  return () => { for (const el of made) releaseInert(el); };
}

/* How the merchant last acted — a pointer or the keyboard — so the focus an overlay hands back
   on close is ringed only when it was a keyboard close (Escape, Enter on a button): a mouse
   click on × must not leave a heavy ring on the row that opened the drawer (MAR-381). */
let lastInput = 'keyboard';
let inputBound = false;
function bindInputModality() {
  if (inputBound || typeof document === 'undefined') return;
  inputBound = true;
  document.addEventListener('keydown', () => { lastInput = 'keyboard'; }, true);
  const pointer = () => { lastInput = 'pointer'; };
  document.addEventListener('pointerdown', pointer, true);
  document.addEventListener('mousedown', pointer, true);
  document.addEventListener('touchstart', pointer, { capture: true, passive: true });
}
function restoreFocus(el) {
  // `focusVisible` (Chrome, Firefox) says whether the browser may paint the ring; where it is
  // unknown the option is ignored and the browser's own heuristic decides, as before.
  el.focus({ preventScroll: true, focusVisible: lastInput === 'keyboard' });
}

/* `dismissible` and `trap` are read through refs: a busy ConfirmDialog flips dismissible
   for the length of its request, and re-running the effect for that would pop the entry,
   return focus to the trigger behind the dialog and push it back. Only open/active
   re-register. `trap: false` (a non-modal drawer) stays on the Escape stack but lets Tab
   leave it — for the Ask dock beside it only: the rest of the page is inert (inertBehind). */
function useOverlay(open, ref, { onClose, dismissible = true, initialFocus, active = true, trap = true }) {
  const closeRef = useRef(onClose); closeRef.current = onClose;
  const dismissRef = useRef(dismissible); dismissRef.current = dismissible;
  const trapRef = useRef(trap); trapRef.current = trap;
  useEffect(() => {
    if (!open || !active) return;
    const entry = {
      ref,
      get dismissible() { return dismissRef.current; },
      get trap() { return trapRef.current; },
      close: () => closeRef.current && closeRef.current(),
    };
    bindInputModality();
    STACK.push(entry);
    lockScroll();
    const restoreInert = trapRef.current ? null : inertBehind(ref.current);
    if (!keyBound) { document.addEventListener('keydown', onDocKey, true); keyBound = true; }
    const prev = document.activeElement;
    const t = setTimeout(() => {
      const target = (initialFocus && initialFocus.current) || ref.current;
      target && target.focus({ preventScroll: true });
    }, 30);
    return () => {
      clearTimeout(t);
      if (restoreInert) restoreInert();
      const i = STACK.indexOf(entry); if (i >= 0) STACK.splice(i, 1);
      if (!STACK.length) unlockScroll();
      if (prev && prev.focus && document.contains(prev)) restoreFocus(prev);
    };
  }, [open, active]);
}
function portal(node, contained) {
  if (contained || typeof document === 'undefined' || !ReactDOM || !ReactDOM.createPortal) return node;
  // `.mv-layer` gives a portalled overlay the base `.mv-root` gives the app (type, box model),
  // without taking part in layout (display: contents). See base.css.
  return ReactDOM.createPortal(<div className="mv-layer">{node}</div>, document.body);
}

/* ---------------- Scrim ---------------- */
function Scrim({ open = false, onClick, layer = 'drawer', contained = false, className = '', style = {} }) {
  return <div className={cx('mv-scrim', open && 'mv-scrim--open', layer === 'modal' && 'mv-scrim--modal', contained && 'mv-scrim--contained', className)}
    style={style} onClick={onClick} aria-hidden="true" />;
}

/* ---------------- Drawer ---------------- */
/* ONE open/close behaviour for every drawer (MAR-381). The panel is always mounted; only
   `--open` flips, so opening slides in and switching records while open replays nothing.
   On close the drawer keeps rendering what it last showed while open — title, eyebrow,
   subtitle, media, footer and children — through the slide-out, and drops it when the
   panel's own `transitionend` fires (fallback: `--dur-drawer` + a margin; reduced motion:
   at once). A screen never guards its content on `open` and never runs its own timer.
   The panel slides inside `.mv-drawer-stage`, a fixed box inset by `--dockw` that clips, so
   the slide can never cross or paint over the Ask dock — whatever `--dockw` does mid-slide.
   Two classes, two jobs: `--open` is the LOGICAL state (visible, focusable, on the stack) and
   lands with `open`; `--in` is the slide (transform) and lands frames later — always, even
   for a drawer that mounts open — once `--dockw` has held still for a frame. Opening a record
   drawer collapses the dock in the mid band (registration → AppShell → `--dockw`), and the
   panel must slide straight to its final rest, never to the 400px-dock rest and then jump. */
const DRAWER_SETTLE_MAX_FRAMES = 8;
function readDockw() {
  if (typeof window === 'undefined' || !window.getComputedStyle) return '';
  return window.getComputedStyle(document.documentElement).getPropertyValue('--dockw').trim();
}
const nextFrame = (fn) => (typeof window !== 'undefined' && window.requestAnimationFrame ? window.requestAnimationFrame(fn) : setTimeout(fn, 16));
const cancelFrame = (h) => (typeof window !== 'undefined' && window.cancelAnimationFrame ? window.cancelAnimationFrame(h) : clearTimeout(h));
const DRAWER_EXIT_MARGIN_MS = 80;
function drawerDurationMs() {
  if (typeof window === 'undefined' || !window.getComputedStyle) return 280;
  const raw = window.getComputedStyle(document.documentElement).getPropertyValue('--dur-drawer').trim();
  const n = parseFloat(raw);
  if (!Number.isFinite(n)) return 280;
  return /ms$/.test(raw) ? n : /s$/.test(raw) ? n * 1000 : n;
}
function prefersReducedMotion() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  const q = window.matchMedia('(prefers-reduced-motion: reduce)');
  return !!(q && q.matches);
}
function Drawer({
  open = false, onClose, onExited, scope, title, eyebrow, subtitle, media, size = 'sm', footer, children,
  dismissible = true, closeLabel = 'Close', initialFocus, contained = false, trapFocus = true, modal = true, id, className = '', style = {},
}) {
  const ref = useRef(null);
  // Focus lands on the drawer's TITLE (Layouts/Drawer contract), not the panel: a focused panel
  // paints its focus ring round the whole drawer. Neither is a control, so neither shows a ring.
  const titleRef = useRef(null);
  const auto = useId();
  const tid = `${id || 'mv-dw' + auto.replace(/:/g, '')}-title`;
  // What the drawer showed while it was last open — rendered through the close.
  const held = useRef(null);
  if (open) held.current = { scope, title, eyebrow, subtitle, media, footer, children };
  // Derived state (React's "storing information from previous renders"): a close starts the
  // exit in the same render, so there is no frame with an empty panel.
  const [prevOpen, setPrevOpen] = React.useState(open);
  const [exiting, setExiting] = React.useState(false);
  if (prevOpen !== open) { setPrevOpen(open); setExiting(!open && held.current != null); }
  // The slide: closed state first, `--in` once at least two frames have passed AND `--dockw`
  // read the same on the last two (capped, so a dock that never settles cannot hold it shut).
  const [entered, setEntered] = React.useState(false);
  if (!open && entered) setEntered(false);
  useEffect(() => {
    if (!open) return undefined;
    let frames = 0, last = null, handle;
    const tick = () => {
      frames += 1;
      const v = readDockw();
      if ((frames >= 2 && v === last) || frames >= DRAWER_SETTLE_MAX_FRAMES) { setEntered(true); return; }
      last = v;
      handle = nextFrame(tick);
    };
    handle = nextFrame(tick);
    return () => cancelFrame(handle);
  }, [open]);
  const exitedRef = useRef(onExited); exitedRef.current = onExited;
  useEffect(() => {
    if (open || !exiting) return undefined;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      held.current = null;
      setExiting(false);
      if (exitedRef.current) exitedRef.current();
    };
    // A close CAUSED by leaving the scope has nothing to show: no empty slide-out, no hold —
    // finish at once so the shell releases the dock in the same beat (MAR-381).
    if (prefersReducedMotion() || !held.current || held.current.scope !== scope) { finish(); return undefined; }
    const panel = ref.current;
    const onEnd = (e) => { if (e.target === panel) finish(); };
    if (panel) panel.addEventListener('transitionend', onEnd);
    const t = setTimeout(finish, drawerDurationMs() + DRAWER_EXIT_MARGIN_MS);
    return () => { done = true; clearTimeout(t); if (panel) panel.removeEventListener('transitionend', onEnd); };
  }, [open, exiting, scope]);
  // `scope` (a workspace): content is held only through a close in the scope it was shown in.
  // A close CAUSED by leaving that scope drops it at once — one workspace's record never
  // slides out under another's name (MAR-205).
  const holding = exiting && held.current && held.current.scope === scope;
  const shown = open ? { title, eyebrow, subtitle, media, footer, children } : holding ? held.current : null;
  // modal={false}: a record drawer that sits BESIDE the Ask dock (overlays.md) — no
  // aria-modal, Tab not trapped; Escape, focus in/out, the scrim and the scroll lock stay.
  useOverlay(open, ref, { onClose, dismissible, initialFocus: initialFocus || titleRef, active: trapFocus && !contained, trap: modal });
  return portal(
    <>
      <Scrim open={open} onClick={dismissible ? onClose : undefined} contained={contained} />
      <div className={cx('mv-drawer-stage', contained && 'mv-drawer-stage--contained')}>
        <aside ref={ref} id={id} role="dialog" aria-modal={open && !contained && modal ? 'true' : undefined} aria-labelledby={tid} aria-hidden={!open || undefined}
          tabIndex={-1} className={cx('mv-drawer-panel', size !== 'sm' && `mv-drawer-panel--${size}`, open && 'mv-drawer-panel--open', open && entered && 'mv-drawer-panel--in', !open && exiting && !holding && 'mv-drawer-panel--gone', contained && 'mv-drawer-panel--contained', className)} style={style}
          data-state={open ? (entered ? 'open' : 'opening') : exiting ? 'closing' : 'closed'}
          // Held content is on screen while it slides out, but no longer the merchant's to use.
          inert={!open && holding ? '' : undefined}>
          <header className="mv-drawer-head">
            {shown && shown.media}
            <div className="mv-drawer-titles">
              {shown && shown.eyebrow && <Eyebrow>{shown.eyebrow}</Eyebrow>}
              <h2 id={tid} ref={titleRef} tabIndex={-1} className="mv-drawer-title">{shown ? shown.title : ''}</h2>
              {shown && shown.subtitle && <div className="mv-drawer-sub">{shown.subtitle}</div>}
            </div>
            <button type="button" className="mv-drawer-x" aria-label={closeLabel} title={closeLabel} onClick={onClose}><Svg d={ICONS.x} sw={2} /></button>
          </header>
          <div className="mv-drawer-body">{shown && shown.children}</div>
          {shown && shown.footer && <footer className="mv-drawer-foot">{shown.footer}</footer>}
        </aside>
      </div>
    </>, contained);
}

/* A screen that holds its own data for a drawer (a selected record it clears on close) keeps
   the last value it showed through the close with this, and passes `onExited` to the Drawer:
   `const { value: shownRecord, onExited } = useDrawerContent(record, open)`. */
function useDrawerContent(value, open) {
  const held = useRef(open && value != null ? value : null);
  const [, bump] = React.useReducer((n) => n + 1, 0);
  if (open && value != null) held.current = value;
  const onExited = useCallback(() => { held.current = null; bump(); }, []);
  return { value: open && value != null ? value : held.current, onExited };
}

/* ---------------- Modal ---------------- */
function Modal({
  open = false, onClose, title, description, size = 'sm', tone = 'default', actions, children,
  dismissible = true, alert = false, initialFocus, contained = false, id, className = '', style = {},
}) {
  const ref = useRef(null);
  const auto = useId();
  const base = id || 'mv-md' + auto.replace(/:/g, '');
  useOverlay(open, ref, { onClose, dismissible, initialFocus, active: !contained });
  return portal(
    <>
      <Scrim open={open} layer="modal" onClick={dismissible && !alert ? onClose : undefined} contained={contained} />
      <div className={cx('mv-modal-wrap', contained && 'mv-modal-wrap--contained')}>
        <div ref={ref} id={id} role={alert ? 'alertdialog' : 'dialog'} aria-modal={open && !contained ? 'true' : undefined}
          aria-labelledby={`${base}-t`} aria-describedby={description ? `${base}-d` : undefined} aria-hidden={!open || undefined} tabIndex={-1}
          className={cx('mv-modal', size !== 'sm' && `mv-modal--${size}`, tone === 'danger' && 'mv-modal--danger', open && 'mv-modal--open', className)} style={style}>
          <h2 id={`${base}-t`} className="mv-modal-title">{title}</h2>
          {description && <p id={`${base}-d`} className="mv-modal-desc">{description}</p>}
          {children && <div className="mv-modal-body">{children}</div>}
          {actions && <div className="mv-modal-foot">{actions}</div>}
        </div>
      </div>
    </>, contained);
}

/* ================= Batch 1: controls ================= */

/* ---------------- IconButton ---------------- */
const IconButton = React.forwardRef(function IconButton({
  icon, label, variant = 'bare', size = 'md', tone = 'default', pressed, badge, busy = false, disabled = false,
  href, onClick, className = '', style = {}, ...rest
}, ref) {
  if (!label && typeof console !== 'undefined') console.warn('IconButton: `label` is required (accessible name + tooltip).');
  const inert = disabled || busy;
  const cls = cx('mv-ibtn', `mv-ibtn--${variant}`, `mv-ibtn--${size}`, tone === 'danger' && 'mv-ibtn--danger', className);
  const inner = <>{busy ? <span className="mv-spin" aria-hidden="true" /> : icon}{badge != null && badge !== false && <span className="mv-ibtn-badge">{badge}</span>}</>;
  const handle = (e) => { if (inert) { e.preventDefault(); return; } onClick && onClick(e); };
  const tip = rest.title === undefined ? label : rest.title || undefined;
  if (href) return <a ref={ref} {...rest} className={cls} style={style} href={inert ? undefined : href} aria-label={label} title={tip} aria-disabled={inert || undefined} onClick={handle}>{inner}</a>;
  return <button ref={ref} {...rest} type="button" className={cls} style={style} aria-label={label} title={tip}
    aria-pressed={pressed === undefined ? undefined : !!pressed} aria-busy={busy || undefined} aria-disabled={busy || undefined}
    disabled={disabled && !busy} onClick={handle}>{inner}</button>;
});

/* ---------------- Badge ---------------- */
function Badge({ children, tone = 'neutral', dot = false, mono = false, count, max = 99, state, className = '', style = {}, ...rest }) {
  if (count !== undefined) {
    const n = typeof count === 'number' && count > max ? `${max}+` : count;
    return <span {...rest} className={cx('mv-count', state && `mv-count--${state}`, className)} style={style}>{n}</span>;
  }
  return <span {...rest} className={cx('mv-badge', tone !== 'neutral' && `mv-badge--${tone}`, mono && 'mv-badge--mono', className)} style={style}>
    {dot && <span className="mv-badge-dot" aria-hidden="true" />}{children}</span>;
}

/* ---------------- Tabs ---------------- */
/* A row of tabs wider than its column scrolls sideways (MAR-381): no scrollbar, a fade on each
   edge that has more behind it, and the selected tab is scrolled into view whenever it changes
   — by click, arrow key or a link. The frame carries the fade; the tablist is the scroller. */
/* The fades follow the row's CONTENT, not only its box (review round 1): a badge count that
   grows widens a tab without resizing the tablist, so the tablist's own ResizeObserver never
   fires. Each tab is observed too, a MutationObserver re-observes them when tabs come and go
   and catches text changes, and a change to the `tabs` prop re-measures after it renders. */
function useTabsOverflow(listRef, value, tabs) {
  const [edges, setEdges] = React.useState({ start: false, end: false });
  const measure = useCallback(() => {
    const el = listRef.current; if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const start = max > 1 && el.scrollLeft > 1, end = max > 1 && el.scrollLeft < max - 1;
    setEdges((e) => (e.start === start && e.end === end ? e : { start, end }));
  }, [listRef]);
  useEffect(() => {
    const el = listRef.current; if (!el) return undefined;
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    let ro = null;
    const observeAll = () => { if (!ro) return; ro.disconnect(); ro.observe(el); Array.from(el.children).forEach((c) => ro.observe(c)); };
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(measure); observeAll(); }
    else if (typeof window !== 'undefined') window.addEventListener('resize', measure);
    let mo = null;
    if (typeof MutationObserver !== 'undefined') {
      mo = new MutationObserver((records) => {
        if (records.some((r) => r.type === 'childList' && r.target === el)) observeAll();
        measure();
      });
      mo.observe(el, { childList: true, subtree: true, characterData: true });
    }
    return () => {
      el.removeEventListener('scroll', measure);
      if (mo) mo.disconnect();
      if (ro) ro.disconnect(); else if (typeof window !== 'undefined') window.removeEventListener('resize', measure);
    };
  }, [measure]);
  // A new `tabs` (a label, a badge, a tab added) re-measures once it has rendered.
  useEffect(() => { measure(); }, [tabs, measure]);
  useEffect(() => {
    const el = listRef.current; if (!el) return;
    const on = el.querySelector('[aria-selected="true"]');
    if (!on || el.scrollWidth <= el.clientWidth) return;
    // Scroll the tablist alone — never the page (scrollIntoView would move the canvas too).
    const l = on.offsetLeft - el.offsetLeft, r = l + on.offsetWidth, pad = 32;
    if (l - pad < el.scrollLeft) el.scrollLeft = Math.max(0, l - pad);
    else if (r + pad > el.scrollLeft + el.clientWidth) el.scrollLeft = r + pad - el.clientWidth;
    measure();
  }, [value, measure]);
  return edges;
}
/* `aside` (MAR-401): one control at the far end of the tab row, standing ON the tab rule — a
   qualifier of every panel (Knowledge's "Who" SegmentedControl), never a tab of its own. The
   rule then runs under both; when the row is too narrow the aside wraps under the tabs. */
function Tabs({ tabs = [], value, onChange, variant = 'underline', label, idBase, aside, className = '', style = {} }) {
  const auto = useId();
  const base = idBase || 'mv-tabs' + auto.replace(/:/g, '');
  const refs = useRef([]);
  const listRef = useRef(null);
  const edges = useTabsOverflow(listRef, value, tabs);
  const enabled = tabs.map((t, i) => (t.disabled ? -1 : i)).filter((i) => i >= 0);
  const move = (i) => { const t = tabs[i]; refs.current[i] && refs.current[i].focus(); onChange && onChange(t.value); };
  const onKey = (e, i) => {
    const pos = enabled.indexOf(i); let next = null;
    if (e.key === 'ArrowRight') next = enabled[(pos + 1) % enabled.length];
    else if (e.key === 'ArrowLeft') next = enabled[(pos - 1 + enabled.length) % enabled.length];
    else if (e.key === 'Home') next = enabled[0];
    else if (e.key === 'End') next = enabled[enabled.length - 1];
    if (next != null) { e.preventDefault(); move(next); }
  };
  const frame = (
    <div className={cx('mv-tabs-frame', `mv-tabs-frame--${variant}`, edges.start && 'mv-tabs-frame--fade-start', edges.end && 'mv-tabs-frame--fade-end', aside == null && className)} style={aside == null ? style : undefined}>
      <div ref={listRef} role="tablist" aria-label={label} className={cx('mv-tabs', `mv-tabs--${variant}`)}>
        {tabs.map((t, i) => {
          const on = t.value === value;
          return (
            <button key={t.value} ref={(el) => (refs.current[i] = el)} type="button" role="tab" id={`${base}-tab-${t.value}`}
              aria-selected={on} aria-controls={`${base}-panel-${t.value}`} tabIndex={on ? 0 : -1} disabled={t.disabled}
              className={cx('mv-tab', on && 'mv-tab--on')} onClick={() => onChange && onChange(t.value)} onKeyDown={(e) => onKey(e, i)}>
              {t.icon}<span className="mv-tab-label">{t.label}</span>{t.badge != null && <Badge count={t.badge} />}
            </button>
          );
        })}
      </div>
    </div>
  );
  if (aside == null) return frame;
  return (
    <div className={cx('mv-tabs-bar', `mv-tabs-bar--${variant}`, className)} style={style}>
      {frame}
      <div className="mv-tabs-aside">{aside}</div>
    </div>
  );
}
/* The panel keeps the system's distance from its tabs (MAR-381): `space-section` under
   underline tabs, `space-row-gap` under segmented ones. A panel that follows its Tabs as a
   sibling gets the right one from CSS; `variant` says it where it does not. */
function TabPanel({ value, active, idBase, variant, children, className = '' }) {
  return <div role="tabpanel" id={`${idBase}-panel-${value}`} aria-labelledby={`${idBase}-tab-${value}`} hidden={active !== value}
    tabIndex={0} className={cx('mv-tabpanel', variant && `mv-tabpanel--${variant}`, className)}>{active === value ? children : null}</div>;
}

/* ---------------- SegmentedControl ---------------- */
function SegmentedControl({ options = [], value, onChange, label, size = 'md', disabled = false, className = '', style = {} }) {
  const refs = useRef([]);
  const opts = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
  const live = opts.map((o, i) => (o.disabled || disabled ? -1 : i)).filter((i) => i >= 0);
  const onKey = (e, i) => {
    const pos = live.indexOf(i); let n = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') n = live[(pos + 1) % live.length];
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') n = live[(pos - 1 + live.length) % live.length];
    if (n != null) { e.preventDefault(); refs.current[n] && refs.current[n].focus(); onChange && onChange(opts[n].value); }
  };
  const sel = opts.findIndex((o) => o.value === value);
  return (
    <div role="radiogroup" aria-label={label} aria-disabled={disabled || undefined} className={cx('mv-seg', size === 'sm' && 'mv-seg--sm', className)} style={style}>
      {opts.map((o, i) => (
        <button key={o.value} ref={(el) => (refs.current[i] = el)} type="button" role="radio" aria-checked={o.value === value}
          tabIndex={(sel === -1 ? i === live[0] : o.value === value) ? 0 : -1} disabled={disabled || o.disabled}
          className={cx('mv-seg-opt', o.value === value && 'mv-seg-opt--on')} onClick={() => onChange && onChange(o.value)} onKeyDown={(e) => onKey(e, i)}>
          {o.icon}{o.label}
        </button>
      ))}
    </div>
  );
}

/* ---------------- Checkbox ---------------- */
function Checkbox({ label, description, checked, defaultChecked, indeterminate = false, onChange, disabled = false, readOnly = false,
  error, id, name, value, className = '', style = {}, inputRef, ...rest }) {
  const auto = useId(); const fid = id || 'mv-cb' + auto.replace(/:/g, '');
  const own = useRef(null);
  useEffect(() => { const el = own.current; if (el) el.indeterminate = !!indeterminate; }, [indeterminate]);
  const setRef = (el) => { own.current = el; if (typeof inputRef === 'function') inputRef(el); else if (inputRef) inputRef.current = el; };
  const descId = description ? `${fid}-d` : undefined; const errId = error ? `${fid}-e` : undefined;
  return (
    <div className={cx('mv-check', disabled && 'mv-check--disabled', readOnly && 'mv-check--readonly', error && 'mv-check--error', className)} style={style}>
      <input ref={setRef} type="checkbox" id={fid} name={name} value={value} className="mv-check-input" checked={checked} defaultChecked={defaultChecked}
        disabled={disabled} aria-readonly={readOnly || undefined} aria-invalid={error ? true : undefined}
        aria-describedby={[errId, descId].filter(Boolean).join(' ') || undefined}
        aria-checked={indeterminate ? 'mixed' : undefined}
        onChange={(e) => { if (readOnly) { e.preventDefault(); return; } onChange && onChange(e); }} {...rest} />
      <span className="mv-check-box" aria-hidden="true"><Svg d={indeterminate ? 'M7 12h10' : ICONS.check} sw={2.4} /></span>
      {(label || description) && <span className="mv-check-text">
        {label && <label htmlFor={fid} className="mv-check-label">{label}</label>}
        {description && <span id={descId} className="mv-check-desc">{description}</span>}
        {error && <span id={errId} className="mv-msg mv-msg--error" role="alert"><Svg d={ICONS.alert} sw={2} /><span>{error}</span></span>}
      </span>}
    </div>
  );
}

/* ---------------- Radio / RadioGroup ---------------- */
function RadioGroup({ legend, name, value, defaultValue, onChange, options = [], hint, error, disabled = false, orientation = 'vertical', className = '', style = {} }) {
  const auto = useId(); const nm = name || 'mv-rg' + auto.replace(/:/g, '');
  const errId = error ? `${nm}-e` : undefined; const hintId = hint ? `${nm}-h` : undefined;
  return (
    <fieldset className={cx('mv-radios', orientation === 'horizontal' && 'mv-radios--row', error && 'mv-radios--error', className)} style={style}
      disabled={disabled} aria-describedby={[errId, hintId].filter(Boolean).join(' ') || undefined}>
      {legend && <legend className="mv-label">{legend}</legend>}
      <div className="mv-radios-list">
        {options.map((o) => {
          const opt = typeof o === 'string' ? { value: o, label: o } : o; const rid = `${nm}-${opt.value}`;
          return (
            <div key={opt.value} className={cx('mv-radio', opt.disabled && 'mv-radio--disabled')}>
              <input type="radio" className="mv-radio-input" id={rid} name={nm} value={opt.value} disabled={opt.disabled}
                checked={value !== undefined ? value === opt.value : undefined} defaultChecked={defaultValue !== undefined ? defaultValue === opt.value : undefined}
                aria-describedby={opt.description ? `${rid}-d` : undefined} onChange={() => onChange && onChange(opt.value)} />
              <span className="mv-radio-dot" aria-hidden="true" />
              <span className="mv-check-text">
                <label htmlFor={rid} className="mv-check-label">{opt.label}</label>
                {opt.description && <span id={`${rid}-d`} className="mv-check-desc">{opt.description}</span>}
              </span>
            </div>
          );
        })}
      </div>
      {error && <div id={errId} className="mv-msg mv-msg--error" role="alert"><Svg d={ICONS.alert} sw={2} /><span>{error}</span></div>}
      {hint && <div id={hintId} className="mv-hint">{hint}</div>}
    </fieldset>
  );
}

/* ---------------- Toggle (switch) ---------------- */
function Toggle({ checked = false, onChange, label, labelHidden = false, description, disabled = false, busy = false, readOnly = false,
  size = 'md', id, className = '', style = {}, ...rest }) {
  const auto = useId(); const fid = id || 'mv-tg' + auto.replace(/:/g, '');
  const inert = disabled || busy || readOnly;
  const fire = () => { if (!inert && onChange) onChange(!checked); };
  const sw = (
    <button {...rest} id={fid} type="button" role="switch" aria-checked={!!checked} aria-busy={busy || undefined}
      aria-disabled={(busy || readOnly || disabled) || undefined} disabled={disabled} aria-label={labelHidden ? label : undefined}
      aria-labelledby={!labelHidden && label ? `${fid}-l` : undefined} aria-describedby={description ? `${fid}-d` : undefined}
      className={cx('mv-switch', size === 'sm' && 'mv-switch--sm', checked && 'mv-switch--on', inert && 'mv-switch--locked')}
      onClick={(e) => { if (inert) { e.preventDefault(); return; } fire(); }}>
      <span className="mv-switch-thumb">{busy ? <span className="mv-spin" /> : checked ? <Svg d={ICONS.check} sw={2.6} /> : null}</span>
    </button>
  );
  if (labelHidden || !label) return <span className={cx('mv-toggle', className)} style={style}>{sw}</span>;
  return (
    <div className={cx('mv-toggle', 'mv-toggle--row', className)} style={style}>
      <span className="mv-check-text">
        <span id={`${fid}-l`} className="mv-toggle-label" onClick={() => document.getElementById(fid) && document.getElementById(fid).focus()}>{label}</span>
        {description && <span id={`${fid}-d`} className="mv-check-desc">{description}</span>}
      </span>
      {sw}
    </div>
  );
}

/* ---------------- Slider ---------------- */
function Slider({ label, value, defaultValue, min = 0, max = 100, step = 1, onChange, format, unit = '', showValue = true,
  minLabel, maxLabel, disabled = false, id, hint, className = '', style = {}, ...rest }) {
  const auto = useId(); const fid = id || 'mv-sl' + auto.replace(/:/g, '');
  const [inner, setInner] = React.useState(defaultValue != null ? defaultValue : min);
  const v = value != null ? value : inner;
  const pct = ((v - min) / (max - min)) * 100;
  const text = format ? format(v) : `${v}${unit}`;
  return (
    <div className={cx('mv-slider', disabled && 'mv-slider--disabled', className)} style={style}>
      {(label || showValue) && <div className="mv-slider-head">
        {label && <label className="mv-label" htmlFor={fid}>{label}</label>}
        {showValue && <output className="mv-slider-value" htmlFor={fid} aria-hidden="true">{text}</output>}
      </div>}
      <input {...rest} id={fid} type="range" className="mv-range" min={min} max={max} step={step} value={v} disabled={disabled}
        aria-valuetext={text} aria-describedby={hint ? `${fid}-h` : undefined} style={{ '--p': `${pct}%` }}
        onChange={(e) => { const n = Number(e.target.value); if (value == null) setInner(n); onChange && onChange(n); }} />
      {(minLabel || maxLabel) && <div className="mv-slider-ends" aria-hidden="true"><span>{minLabel}</span><span>{maxLabel}</span></div>}
      {hint && <div id={`${fid}-h`} className="mv-hint">{hint}</div>}
    </div>
  );
}

/* ================= Batch 2: feedback ================= */
const TONE_ICON = {
  info: ['M12 8h.01', 'M11 12h1v5h1', 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z'],
  success: ['M5 12.5l4.2 4.2L19 7'],
  attention: ['M12 7v6', 'M12 16.5v.01', 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z'],
  danger: ICONS.alert,
};

/* ---------------- Alert ---------------- */
function Alert({ tone = 'info', title, children, actions, icon, orb = false, variant = 'inline', onDismiss, dismissLabel = 'Dismiss',
  live, className = '', style = {}, ...rest }) {
  const role = live === undefined ? undefined : live === 'assertive' ? 'alert' : 'status';
  const lead = orb ? <Orb size={16} /> : icon === null ? null : (icon || <span className="mv-alert-ic"><Svg d={TONE_ICON[tone] || TONE_ICON.info} sw={2} /></span>);
  return (
    <div {...rest} role={role !== undefined ? role : rest.role} className={cx('mv-alert', `mv-alert--${tone}`, variant === 'banner' && 'mv-alert--banner', className)} style={style}>
      {lead}
      <div className="mv-alert-text">
        {title && <div className="mv-alert-title">{title}</div>}
        {children && <div className="mv-alert-body">{children}</div>}
      </div>
      {actions && <div className="mv-alert-actions">{actions}</div>}
      {onDismiss && <IconButton size="sm" icon={<Svg d={ICONS.x} sw={2} />} label={dismissLabel} onClick={onDismiss} className="mv-alert-x" />}
    </div>
  );
}

/* ---------------- ErrorSummary (form-level inline validation) ---------------- */
const ErrorSummary = React.forwardRef(function ErrorSummary({ title = 'Fix these to save', errors = [], className = '' }, ref) {
  if (!errors.length) return null;
  return (
    <div ref={ref} tabIndex={-1} role="alert" className={cx('mv-alert', 'mv-alert--danger', 'mv-errsum', className)}>
      <span className="mv-alert-ic"><Svg d={TONE_ICON.danger} sw={2} /></span>
      <div className="mv-alert-text">
        <div className="mv-alert-title">{title}</div>
        <ul className="mv-errsum-list">{errors.map((e) => <li key={e.id}><a href={`#${e.id}`} onClick={(ev) => { const el = document.getElementById(e.id); if (el) { ev.preventDefault(); el.focus(); el.scrollIntoView({ block: 'center' }); } }}>{e.message}</a></li>)}</ul>
      </div>
    </div>
  );
});

/* ---------------- Toasts ---------------- */
const ToastCtx = React.createContext(null);
function Toast({ tone = 'success', title, body, action, onClose, closeLabel = 'Dismiss', className = '' }) {
  return (
    <div className={cx('mv-toast', `mv-toast--${tone}`, className)} role={tone === 'danger' ? 'alert' : 'status'}>
      <span className="mv-alert-ic"><Svg d={TONE_ICON[tone] || TONE_ICON.info} sw={2} /></span>
      <div className="mv-alert-text">{title && <div className="mv-alert-title">{title}</div>}{body && <div className="mv-alert-body">{body}</div>}</div>
      {action && <button type="button" className="mv-toast-act" onClick={() => { action.onClick && action.onClick(); onClose && onClose(); }}>{action.label}</button>}
      {onClose && <IconButton size="sm" icon={<Svg d={ICONS.x} sw={2} />} label={closeLabel} onClick={onClose} />}
    </div>
  );
}
function ToastProvider({ children, max = 3, duration = 6000 }) {
  const [list, setList] = React.useState([]);
  const timers = useRef({});
  const remove = useCallback((id) => { clearTimeout(timers.current[id]); delete timers.current[id]; setList((l) => l.filter((t) => t.id !== id)); }, []);
  const arm = useCallback((t) => { if (t.tone === 'danger' || t.persist) return; clearTimeout(timers.current[t.id]); timers.current[t.id] = setTimeout(() => remove(t.id), t.duration || duration); }, [duration, remove]);
  const toast = useCallback((t) => { const id = Math.random().toString(36).slice(2); const item = { tone: 'success', ...t, id }; setList((l) => [...l, item].slice(-max)); arm(item); return id; }, [arm, max]);
  const pause = (id) => clearTimeout(timers.current[id]);
  const api = React.useMemo(() => ({ toast, dismiss: remove }), [toast, remove]);
  return (
    <ToastCtx.Provider value={api}>
      {children}
      {portal(<div className="mv-toasts" aria-live="polite" aria-relevant="additions">
        {list.map((t) => <div key={t.id} onMouseEnter={() => pause(t.id)} onMouseLeave={() => arm(t)} onFocus={() => pause(t.id)} onBlur={() => arm(t)}>
          <Toast {...t} onClose={() => remove(t.id)} /></div>)}
      </div>, false)}
    </ToastCtx.Provider>
  );
}
function useToast() { const c = React.useContext(ToastCtx); return c || { toast: () => {}, dismiss: () => {} }; }

/* ---------------- States: empty · loading · degraded · error ---------------- */
function EmptyState({ eyebrow, title, children, action, size = 'md', className = '' }) {
  return (
    <div className={cx('mv-state', 'mv-state--empty', size === 'sm' && 'mv-state--sm', className)}>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      {title && <div className="mv-state-title">{title}</div>}
      {children && <div className="mv-state-body">{children}</div>}
      {action && <div className="mv-state-action">{action}</div>}
    </div>
  );
}
function LoadingState({ variant = 'inline', caption = 'Reading…', steps, className = '' }) {
  if (variant === 'page') {
    return (
      <div className={cx('mv-loading', className)} role="status" aria-live="polite">
        <div className="mv-loading-orb"><span className="ring" /><span className="ring" /><span className="ring" /><Orb size={64} float /></div>
        <div className="mv-loading-cap">{caption}</div>
        {steps && <div className="mv-loading-steps" aria-hidden="true">{steps.slice(0, 3).map((s) => <span key={s}>{s}</span>)}</div>}
      </div>
    );
  }
  return <div className={cx('mv-state-inline', className)} role="status" aria-live="polite"><span className="mv-spin" aria-hidden="true" />{caption}</div>;
}
function DegradedState({ title, children, at, onRetry, retryLabel = 'Check again', className = '' }) {
  return (
    <div className={cx('mv-state', 'mv-state--degraded', className)} role="status">
      <div className="mv-state-title"><span className="mv-badge-dot" aria-hidden="true" />{title}{at && <span className="mv-state-at"> · as of {at}</span>}</div>
      {children && <div className="mv-state-body">{children}</div>}
      {onRetry && <div className="mv-state-action"><Button size="sm" variant="ghost" onClick={onRetry}>{retryLabel}</Button></div>}
    </div>
  );
}
function ErrorState({ title = "Couldn't load this.", children, onRetry, retryLabel = 'Try again', busy = false, className = '' }) {
  return (
    <div className={cx('mv-state', 'mv-state--error', className)} role="alert">
      <div className="mv-state-title"><Svg d={TONE_ICON.danger} sw={2} />{title}</div>
      {children && <div className="mv-state-body">{children}</div>}
      {onRetry && <div className="mv-state-action"><Button size="sm" variant="ghost" busy={busy} busyLabel="Trying again…" onClick={onRetry}>{retryLabel}</Button></div>}
    </div>
  );
}

/* ---------------- SaveBar ---------------- */
function SaveBar({ state = 'clean', note, saveLabel = 'Save changes', onSave, onCancel, cancelLabel = 'Cancel', error, placement = 'sticky', blocked = false, blockedNote, className = '', style = {} }) {
  const dirty = state === 'dirty' || state === 'error';
  // `blocked`: there are changes, but one of them is still settling (an upload, a pending suggestion).
  const msg = blocked && state !== 'saving' ? (blockedNote || 'Finishing up…') : state === 'saving' ? 'Saving…' : state === 'saved' ? 'Saved' : state === 'error' ? (error || "Couldn't save. Your changes are still here.") : state === 'dirty' ? 'Unsaved changes' : (note || 'No changes yet');
  const ic = state === 'saved' ? ICONS.check : state === 'error' ? ICONS.alert : null;
  return (
    <div className={cx('mv-savebar', `mv-savebar--${placement}`, `mv-savebar--${state}`, className)} style={style}>
      <span className="mv-savebar-note" role={state === 'error' ? 'alert' : 'status'}>
        {state === 'dirty' && <span className="mv-badge-dot" aria-hidden="true" />}{ic && <Svg d={ic} sw={2} />}<span>{msg}</span>
      </span>
      {onCancel && <Button variant="ghost" size="sm" onClick={onCancel} disabled={state === 'saving'}>{cancelLabel}</Button>}
      <Button size="sm" onClick={onSave} busy={state === 'saving'} busyLabel="Saving…" disabled={(blocked || !dirty) && state !== 'saving'}>{saveLabel}</Button>
    </div>
  );
}

/* ---------------- ConfirmDialog ---------------- */
function ConfirmDialog({ open, onCancel, onConfirm, title, description, confirmLabel, cancelLabel = 'Cancel', tone = 'danger', busy = false, error, children }) {
  const safe = useRef(null);
  return (
    <Modal open={open} onClose={busy ? undefined : onCancel} alert tone={tone} title={title} description={description} initialFocus={safe} dismissible={!busy}
      actions={<><Button ref={safe} variant="ghost" onClick={onCancel} disabled={busy}>{cancelLabel}</Button>
        <Button variant={tone === 'danger' ? 'danger' : 'gold'} busy={busy} busyLabel={`${confirmLabel}…`} onClick={onConfirm}>{confirmLabel}</Button></>}>
      {children}{error && <div className="mv-msg mv-msg--error" role="alert"><Svg d={ICONS.alert} sw={2} /><span>{error}</span></div>}
    </Modal>
  );
}

/* ================= Batch 3: structure & navigation ================= */
ICONS.search = ['M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14z', 'M21 21l-5-5'];
ICONS.chev = 'M6 9l6 6 6-6';
ICONS.left = 'M15 6l-6 6 6 6';
ICONS.right = 'M9 6l6 6-6 6';

/* ---------------- PageHeader & Section ---------------- */
function PageHeader({ eyebrow, title, sub, actions, className = '' }) {
  return (
    <header className={cx('mv-pghead', className)}>
      <div className="mv-pghead-text">
        {eyebrow && <Eyebrow size="page">{eyebrow}</Eyebrow>}
        <h1 className="mv-pghead-title">{title}</h1>
        {sub && <p className="mv-pghead-sub">{sub}</p>}
      </div>
      {actions && <div className="mv-pghead-actions">{actions}</div>}
    </header>
  );
}
function Section({ title, aside, description, children, id, as: Tag = 'section', className = '' }) {
  const auto = useId(); const hid = (id || 'mv-sec' + auto.replace(/:/g, '')) + '-h';
  return (
    <Tag className={cx('mv-section', className)} aria-labelledby={title ? hid : undefined} id={id}>
      {title && <div className="mv-section-head"><h2 id={hid} className="mv-section-title">{title}</h2>{aside && <div className="mv-section-aside">{aside}</div>}</div>}
      {description && <p className="mv-section-desc">{description}</p>}
      {children}
    </Tag>
  );
}

/* ---------------- SearchField ---------------- */
function SearchField({ value, onChange, onSubmit, placeholder = 'Search', label = 'Search', size = 'md', busy = false, shortcut, resultsText, className = '', style = {}, inputRef, ...rest }) {
  const own = useRef(null);
  const setRef = (el) => { own.current = el; if (typeof inputRef === 'function') inputRef(el); else if (inputRef) inputRef.current = el; };
  useEffect(() => {
    if (!shortcut) return;
    const h = (e) => { const t = e.target; if (e.key === shortcut && !(t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable))) { e.preventDefault(); own.current && own.current.focus(); } };
    document.addEventListener('keydown', h); return () => document.removeEventListener('keydown', h);
  }, [shortcut]);
  return (
    <div role="search" className={cx('mv-search', size === 'lg' && 'mv-search--lg', className)} style={style}>
      <form onSubmit={(e) => { e.preventDefault(); onSubmit && onSubmit(value); }} className="mv-search-form">
        {busy ? <span className="mv-spin" aria-hidden="true" /> : <Svg d={ICONS.search} sw={1.8} />}
        <input {...rest} ref={setRef} type="search" className="mv-search-input" aria-label={label} placeholder={placeholder} value={value}
          onChange={(e) => onChange && onChange(e.target.value)} onKeyDown={(e) => { if (e.key === 'Escape' && value) { e.preventDefault(); e.stopPropagation(); onChange && onChange(''); } }} />
        {value ? <button type="button" className="mv-search-clear" aria-label="Clear search" onClick={() => { onChange && onChange(''); own.current && own.current.focus(); }}><Svg d={ICONS.x} sw={2} /></button>
          : shortcut ? <kbd className="mv-kbd" aria-hidden="true">{shortcut}</kbd> : null}
      </form>
      {resultsText != null && <span className="mv-sr" role="status" aria-live="polite">{resultsText}</span>}
    </div>
  );
}

/* ---------------- Popover plumbing ---------------- */
function useDismiss(open, refs, onClose) {
  const cb = useRef(onClose); cb.current = onClose;
  useEffect(() => {
    if (!open) return;
    const down = (e) => { if (refs.every((r) => r.current && !r.current.contains(e.target))) cb.current && cb.current('outside'); };
    const key = (e) => { if (e.key === 'Escape') { e.stopPropagation(); cb.current && cb.current('escape'); } };
    document.addEventListener('mousedown', down); document.addEventListener('keydown', key, true);
    return () => { document.removeEventListener('mousedown', down); document.removeEventListener('keydown', key, true); };
  }, [open]);
}

/* ---------------- Popover ---------------- */
function Popover({ trigger, children, label, placement = 'bottom-start', open: openProp, onOpenChange, width = 268, className = '' }) {
  const [inner, setInner] = React.useState(false);
  const open = openProp != null ? openProp : inner;
  const set = (v) => { if (openProp == null) setInner(v); onOpenChange && onOpenChange(v); };
  const wrap = useRef(null), panel = useRef(null), btn = useRef(null);
  const auto = useId(); const pid = 'mv-pop' + auto.replace(/:/g, '');
  useDismiss(open, [wrap], (why) => { set(false); if (why === 'escape') btn.current && btn.current.focus(); });
  useEffect(() => { if (open) setTimeout(() => { const f = panel.current && panel.current.querySelector('a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])'); (f || panel.current) && (f || panel.current).focus(); }, 20); }, [open]);
  const t = React.cloneElement(trigger, { ref: btn, 'aria-haspopup': 'dialog', 'aria-expanded': open, 'aria-controls': pid, onClick: (e) => { trigger.props.onClick && trigger.props.onClick(e); set(!open); } });
  return (
    <span ref={wrap} className={cx('mv-anchor', className)}>
      {t}
      <div ref={panel} id={pid} role="dialog" aria-label={label} tabIndex={-1} hidden={!open}
        className={cx('mv-pop', `mv-pop--${placement}`, open && 'mv-pop--open')} style={{ width }}>{children}</div>
    </span>
  );
}

/* ---------------- Menu items ----------------
   What Menu renders, usable on their own inside a Popover (the profile panel): a row with an
   optional icon, name, description, check and shortcut. Inside Menu (`inMenu`) the row is a
   roving `menuitem`/`menuitemradio`; standalone it is a plain button (onClick), link (href)
   or, with neither, a static row (the current account). */
function MenuItem({ icon, label, description, checked, tone, shortcut, href, disabled = false, onClick, inMenu = false,
  className = '', children, ...rest }) {
  const Tag = href ? 'a' : (onClick || inMenu) ? 'button' : 'div';
  const menuRole = checked !== undefined ? 'menuitemradio' : 'menuitem';
  return (
    <Tag {...rest} role={inMenu ? menuRole : rest.role} tabIndex={inMenu ? -1 : rest.tabIndex} href={href}
      type={Tag === 'button' ? 'button' : undefined}
      aria-checked={inMenu && checked !== undefined ? !!checked : undefined}
      aria-disabled={disabled || undefined}
      className={cx('mv-menu-item', tone === 'danger' && 'mv-menu-item--danger', checked && 'mv-menu-item--on', className)}
      onClick={onClick ? (e) => { if (disabled) { e.preventDefault(); return; } onClick(e); } : undefined}>
      {icon && <span className="mv-menu-ic">{icon}</span>}
      <span className="mv-menu-text"><span className="mv-menu-name">{label}</span>{description && <span className="mv-menu-desc">{description}</span>}</span>
      {children}
      {checked !== undefined && <span className="mv-menu-check" aria-hidden="true">{checked ? <Svg d={ICONS.check} sw={2} /> : null}</span>}
      {shortcut && <kbd className="mv-kbd" aria-hidden="true">{shortcut}</kbd>}
    </Tag>
  );
}
function MenuSeparator() { return <div role="separator" className="mv-menu-sep" />; }
function MenuLabel({ children, role }) { return <div role={role} className="mv-menu-label">{children}</div>; }

/* ---------------- Menu ---------------- */
function Menu({ trigger, items = [], label, placement = 'bottom-end', width = 268, onSelect, className = '' }) {
  const [open, setOpen] = React.useState(false);
  const wrap = useRef(null), btn = useRef(null), list = useRef(null);
  const auto = useId(); const mid = 'mv-menu' + auto.replace(/:/g, '');
  const focusables = () => Array.from((list.current && list.current.querySelectorAll('[role^="menuitem"]:not([aria-disabled="true"])')) || []);
  const close = (refocus = true) => { setOpen(false); if (refocus) btn.current && btn.current.focus(); };
  useDismiss(open, [wrap], (why) => close(why === 'escape'));
  const openAt = (where) => { setOpen(true); setTimeout(() => { const f = focusables(); (where === 'last' ? f[f.length - 1] : f[0]) && (where === 'last' ? f[f.length - 1] : f[0]).focus(); }, 10); };
  const onKey = (e) => {
    const f = focusables(); const i = f.indexOf(document.activeElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); (f[(i + 1) % f.length] || f[0]).focus(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); (f[(i - 1 + f.length) % f.length] || f[f.length - 1]).focus(); }
    else if (e.key === 'Home') { e.preventDefault(); f[0] && f[0].focus(); }
    else if (e.key === 'End') { e.preventDefault(); f[f.length - 1] && f[f.length - 1].focus(); }
    else if (e.key === 'Tab') { close(false); }
    else if (e.key.length === 1 && /\S/.test(e.key)) { const k = e.key.toLowerCase(); const start = i + 1; const hit = f.slice(start).concat(f.slice(0, start)).find((el) => (el.textContent || '').trim().toLowerCase().startsWith(k)); hit && hit.focus(); }
  };
  const t = React.cloneElement(trigger, { ref: btn, 'aria-haspopup': 'menu', 'aria-expanded': open, 'aria-controls': mid,
    onClick: (e) => { trigger.props.onClick && trigger.props.onClick(e); open ? close(false) : openAt('first'); },
    onKeyDown: (e) => { if (e.key === 'ArrowDown') { e.preventDefault(); openAt('first'); } if (e.key === 'ArrowUp') { e.preventDefault(); openAt('last'); } } });
  return (
    <span ref={wrap} className={cx('mv-anchor', className)}>
      {t}
      <div ref={list} id={mid} role="menu" aria-label={label} hidden={!open} onKeyDown={onKey}
        className={cx('mv-pop', 'mv-menu', `mv-pop--${placement}`, open && 'mv-pop--open')} style={{ width }}>
        {items.map((it, i) => {
          if (it.type === 'separator') return <MenuSeparator key={i} />;
          if (it.type === 'label') return <MenuLabel key={i} role="presentation">{it.label}</MenuLabel>;
          const act = () => { if (it.disabled) return; it.onSelect && it.onSelect(); onSelect && onSelect(it); close(); };
          return (
            <MenuItem key={it.key || i} inMenu icon={it.icon} label={it.label} description={it.description} checked={it.checked}
              tone={it.tone} shortcut={it.shortcut} href={it.href} disabled={it.disabled}
              onClick={(e) => { if (!it.href) e.preventDefault(); act(); }} />
          );
        })}
      </div>
    </span>
  );
}

/* ---------------- Tooltip ---------------- */
function Tooltip({ content, children, placement = 'top', delay = 500 }) {
  const [show, setShow] = React.useState(false);
  const timer = useRef(null);
  const auto = useId(); const tid = 'mv-tip' + auto.replace(/:/g, '');
  const on = (now) => { clearTimeout(timer.current); timer.current = setTimeout(() => setShow(true), now ? 0 : delay); };
  const off = () => { clearTimeout(timer.current); setShow(false); };
  useEffect(() => { if (!show) return; const k = (e) => { if (e.key === 'Escape') setShow(false); }; document.addEventListener('keydown', k); return () => document.removeEventListener('keydown', k); }, [show]);
  const child = React.Children.only(children);
  const t = React.cloneElement(child, { title: '', 'aria-describedby': cx(child.props['aria-describedby'], tid),
    onMouseEnter: (e) => { child.props.onMouseEnter && child.props.onMouseEnter(e); on(false); }, onMouseLeave: (e) => { child.props.onMouseLeave && child.props.onMouseLeave(e); off(); },
    onFocus: (e) => { child.props.onFocus && child.props.onFocus(e); on(true); }, onBlur: (e) => { child.props.onBlur && child.props.onBlur(e); off(); } });
  return <span className="mv-anchor" onMouseEnter={() => show && clearTimeout(timer.current)}>{t}<span id={tid} role="tooltip" className={cx('mv-tip', `mv-tip--${placement}`, show && 'mv-tip--open')}>{content}</span></span>;
}

/* ---------------- FilterMenu ---------------- */
function FilterMenu({ field, options = [], selected = [], onChange, allLabel = 'All', className = '' }) {
  const [open, setOpen] = React.useState(false);
  const wrap = useRef(null), btn = useRef(null), panel = useRef(null);
  const auto = useId(); const pid = 'mv-flt' + auto.replace(/:/g, '');
  useDismiss(open, [wrap], (why) => { setOpen(false); if (why === 'escape') btn.current && btn.current.focus(); });
  useEffect(() => { if (open) setTimeout(() => { const f = panel.current && panel.current.querySelector('input'); f && f.focus(); }, 10); }, [open]);
  const labels = options.filter((o) => selected.includes(o.value)).map((o) => o.label);
  const summary = labels.length === 0 ? allLabel : labels.length === 1 ? labels[0] : `${labels.length} selected`;
  const toggle = (v) => onChange && onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  return (
    <span ref={wrap} className={cx('mv-anchor', className)}>
      <button ref={btn} type="button" className={cx('mv-filter', selected.length > 0 && 'mv-filter--on')} aria-haspopup="true" aria-expanded={open} aria-controls={pid}
        aria-label={`${field}: ${summary}`} onClick={() => setOpen(!open)}>
        <span className="mv-filter-field">{field}</span><span className="mv-filter-sum">{summary}</span><Svg d={ICONS.chev} sw={2} />
      </button>
      <div ref={panel} id={pid} role="group" aria-label={field} hidden={!open} className={cx('mv-pop', 'mv-pop--bottom-start', open && 'mv-pop--open')} style={{ width: 262 }}>
        <div className="mv-filter-list">{options.map((o) => <Checkbox key={o.value} label={o.label} description={o.description} checked={selected.includes(o.value)} onChange={() => toggle(o.value)} />)}</div>
        <div className="mv-filter-foot"><button type="button" className="mv-linkbtn" disabled={!selected.length} onClick={() => onChange && onChange([])}>Clear</button><span className="t-chip mv-filter-count">{selected.length ? `${selected.length} of ${options.length}` : 'Showing all'}</span></div>
      </div>
    </span>
  );
}
function FilterBar({ children, count, onClearAll, active = 0, className = '' }) {
  return (
    <div className={cx('mv-filterbar', className)} role="toolbar" aria-label="Filters">
      {children}
      <span className="mv-spring" />
      {active > 0 && onClearAll && <button type="button" className="mv-linkbtn" onClick={onClearAll}>Clear all</button>}
      {count != null && <span className="mv-filterbar-count" role="status" aria-live="polite">{count}</span>}
    </div>
  );
}

/* ---------------- Pagination & LoadMore ---------------- */
function Pagination({ offset = 0, limit = 50, total, onPageChange, label = 'Pagination', className = '' }) {
  const from = total === 0 ? 0 : offset + 1; const to = Math.min(offset + limit, total);
  return (
    <nav aria-label={label} className={cx('mv-pager', className)}>
      <span className="mv-pager-range" aria-live="polite"><b>{from}–{to}</b> of {total}</span>
      <IconButton variant="outline" size="sm" icon={<Svg d={ICONS.left} sw={2} />} label="Previous page" disabled={offset <= 0} onClick={() => onPageChange && onPageChange(Math.max(0, offset - limit))} />
      <IconButton variant="outline" size="sm" icon={<Svg d={ICONS.right} sw={2} />} label="Next page" disabled={offset + limit >= total} onClick={() => onPageChange && onPageChange(offset + limit)} />
    </nav>
  );
}
function LoadMore({ hasMore, onLoad, state = 'idle', note, label = 'Show more', shown, className = '' }) {
  if (!hasMore && state !== 'error') return shown ? <div className={cx('mv-loadmore', 'mv-loadmore--end', className)}>{shown}</div> : null;
  return (
    <div className={cx('mv-loadmore', className)}>
      {note && <div className="mv-loadmore-note">{note}</div>}
      {state === 'error' && <div className="mv-msg mv-msg--error" role="alert"><Svg d={ICONS.alert} sw={2} /><span>Couldn't load more just now.</span></div>}
      <Button variant="ghost" size="sm" busy={state === 'loading'} busyLabel="Loading…" onClick={onLoad}>{state === 'error' ? 'Try again' : label}</Button>
      {shown && <span className="mv-loadmore-note" role="status">{shown}</span>}
    </div>
  );
}

/* ---------------- DataTable ---------------- */
/* `stack`: below a 520px container a table of ≤ 3 meaningful columns becomes a card list
   (README; MAR-381). 'auto' counts the columns not marked `secondary`; a secondary column still
   shows in the card, as a labelled line. */
function DataTable({ columns = [], rows = [], rowKey = 'id', sort, onSort, onRowOpen, selectedKey, zebra = false, caption, captionHidden = true,
  stickyFirst = true, stack = 'auto', state, emptyText = 'Nothing here yet.', className = '' }) {
  const stacks = stack === 'auto' ? columns.filter((c) => !c.secondary).length <= 3 : !!stack;
  const ariaSort = (c) => (sort && sort.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : c.sortable ? 'none' : undefined);
  const next = (c) => (sort && sort.key === c.key && sort.dir === 'asc' ? 'desc' : 'asc');
  return (
    <div className={cx('mv-tablewrap', stickyFirst && 'mv-tablewrap--sticky', stacks && 'mv-tablewrap--stack', className)} tabIndex={0} role="region" aria-label={caption}>
      <table className={cx('mv-table', zebra && 'mv-table--zebra')}>
        {caption && <caption className={captionHidden ? 'mv-sr' : 'mv-table-cap'}>{caption}</caption>}
        <thead><tr>{columns.map((c) => (
          <th key={c.key} scope="col" aria-sort={ariaSort(c)} className={cx(c.align === 'right' && 'num')} style={{ width: c.width }}>
            {c.sortable ? <button type="button" className={cx('mv-th-sort', sort && sort.key === c.key && 'mv-th-sort--on')} onClick={() => onSort && onSort({ key: c.key, dir: next(c) })}>
              {c.label}<span className={cx('mv-caret', sort && sort.key === c.key && sort.dir === 'asc' && 'mv-caret--asc')} aria-hidden="true" /></button> : c.label}
          </th>))}</tr></thead>
        <tbody>
          {state === 'loading' && <tr><td colSpan={columns.length}><LoadingState caption="Reading…" /></td></tr>}
          {state === 'error' && <tr><td colSpan={columns.length}><ErrorState title="Couldn't load these rows." /></td></tr>}
          {!state && rows.length === 0 && <tr><td colSpan={columns.length} className="mv-table-empty">{emptyText}</td></tr>}
          {!state && rows.map((r) => {
            const k = r[rowKey]; const sel = selectedKey != null && selectedKey === k;
            return (
              <tr key={k} className={cx(sel && 'is-selected', onRowOpen && 'is-openable')}
                onClick={onRowOpen ? (e) => { if (e.target.closest('a,button')) return; onRowOpen(r); } : undefined}>
                {columns.map((c, ci) => {
                  const v = c.render ? c.render(r) : r[c.key];
                  const cell = ci === 0 && onRowOpen ? <button type="button" className="mv-row-open" aria-current={sel ? 'true' : undefined} onClick={() => onRowOpen(r)}>{v}</button> : v;
                  return ci === 0 ? <th key={c.key} scope="row" className={cx(c.align === 'right' && 'num', c.className)}>{cell}</th>
                    : <td key={c.key} className={cx(c.align === 'right' && 'num', c.className)} data-label={typeof c.label === 'string' ? c.label : undefined}>{cell}</td>;
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ---------------- Navigation: Rail & BottomBar ----------------
   A destination is a link (`href`); a utility (Ask) is a button (`onClick`, no href) —
   Navigation README. On a link, `onClick` takes over the navigation (client-side routing):
   the default is prevented and the handler runs. `variant="bar"` is the BottomBar's item. */
function NavItem({ label, icon, href, onClick, current = false, badge, title, variant = 'rail', className = '' }) {
  const bar = variant === 'bar';
  const cls = cx(bar ? 'mv-bbitem' : 'mv-navitem', current && (bar ? 'mv-bbitem--on' : 'mv-navitem--on'), className);
  const badgeEl = badge != null && <span className="mv-navitem-badge"><Badge count={badge} /></span>;
  const body = bar
    ? <><span className="mv-navitem-ic">{icon}{badgeEl}</span><span className="mv-bbitem-label">{label}</span></>
    : <><span className="mv-navitem-ic">{icon}</span><span className="mv-navitem-label">{label}</span>{badgeEl}</>;
  if (href == null) {
    return <button type="button" className={cls} title={title} onClick={onClick}>{body}</button>;
  }
  return (
    <a href={href} className={cls} title={title} aria-current={current ? 'page' : undefined}
      onClick={onClick ? (e) => { e.preventDefault(); onClick(e); } : undefined}>{body}</a>
  );
}
const navClick = (it, onNavigate) => (it.onClick ? () => it.onClick(it) : onNavigate ? () => onNavigate(it) : undefined);
const navHref = (it) => (it.href != null ? it.href : it.onClick ? undefined : '#');
function NavRail({ items = [], current, onNavigate, footer, brand, label = 'Main', className = '' }) {
  return (
    <nav aria-label={label} className={cx('mv-navrail', className)}>
      {brand !== undefined ? brand : <Orb size={30} label="Marva" />}
      <ul className="mv-navrail-list">{items.map((it) => (
        <li key={it.key}><NavItem label={it.label} icon={it.icon} badge={it.badge} title={it.title} href={navHref(it)}
          current={current === it.key} onClick={navClick(it, onNavigate)} /></li>))}</ul>
      <span className="mv-spring" />
      {footer && <div className="mv-navrail-foot">{footer}</div>}
    </nav>
  );
}
function BottomBar({ items = [], current, onNavigate, label = 'Main', className = '' }) {
  return (
    <nav aria-label={label} className={cx('mv-bottombar', className)}>
      {items.map((it) => (
        <NavItem key={it.key} variant="bar" label={it.label} icon={it.icon} badge={it.badge} title={it.title} href={navHref(it)}
          current={current === it.key} onClick={navClick(it, onNavigate)} />))}
    </nav>
  );
}

/* ================= Gap closers: data display ================= */
function Meter({ label, value, max = 100, included, valueText, tone = 'gold', size = 'md', layout = 'stacked', muted = false, className = '' }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  const text = valueText != null ? valueText : `${Math.round(pct)}%`;
  // `included`: the share of `max` the plan covers. Past it the fill is drawn as overage — a
  // second, hatched segment — so a metered allowance reads as "plan, then over, then the cap".
  const split = included != null && included > 0 && value > included;
  const inPct = split ? Math.max(0, Math.min(100, (included / max) * 100)) : pct;
  const fill = cx('mv-meter-fill', `mv-meter-fill--${tone}`, muted && 'mv-meter-fill--muted');
  const bar = <span className={cx('mv-meter-track', size === 'sm' && 'mv-meter-track--sm', split && 'mv-meter-track--split')} role="meter" aria-valuemin={0} aria-valuemax={max} aria-valuenow={value} aria-valuetext={text} aria-label={typeof label === 'string' ? label : undefined}>
    <span className={fill} style={{ width: `${inPct}%` }} />
    {split && <span className={cx(fill, 'mv-meter-fill--over')} style={{ width: `${pct - inPct}%` }} />}</span>;
  // Inline is all phrasing content — spans, laid out by `.mv-meter`'s flex — so it can sit in an
  // EntryRow `<button>` (Least sure) without a `<div>` the button may not hold (MAR-381).
  if (layout === 'inline') return <span className={cx('mv-meter', 'mv-meter--inline', className)}><span className="mv-meter-label">{label}</span>{bar}<span className="mv-meter-value" aria-hidden="true">{text}</span></span>;
  return <div className={cx('mv-meter', className)}><div className="mv-meter-head"><span className="mv-meter-label">{label}</span><span className="mv-meter-value" aria-hidden="true">{text}</span></div>{bar}</div>;
}
function Avatar({ name = '', src, size = 'md', shape = 'circle', tone = 'neutral', label, className = '' }) {
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
  return <span className={cx('mv-avatar', `mv-avatar--${size}`, `mv-avatar--${shape}`, `mv-avatar--${tone}`, className)} role={label === false ? undefined : 'img'} aria-label={label === false ? undefined : (label || name)} aria-hidden={label === false || undefined}>
    {src ? <img src={src} alt="" /> : initials}</span>;
}
function KeyValue({ items = [], mono = false, columns = 1, className = '' }) {
  return <dl className={cx('mv-kv', mono && 'mv-kv--mono', columns === 2 && 'mv-kv--2', className)}>{items.map((it, i) => <div key={i} className="mv-kv-row"><dt>{it.label}</dt><dd className={cx(it.empty && 'mv-kv-empty')}>{it.value == null || it.value === '' ? '—' : it.value}</dd></div>)}</dl>;
}
function Timeline({ items = [], label, className = '' }) {
  return <ol className={cx('mv-timeline', className)} aria-label={label}>{items.map((it, i) => <li key={i} className="mv-tl-item"><time className="mv-tl-when" dateTime={it.datetime}>{it.when}</time><div className="mv-tl-body">{it.title && <div className="mv-tl-title">{it.title}</div>}{it.detail && <div className="mv-tl-detail">{it.detail}</div>}</div></li>)}</ol>;
}
function Stepper({ steps = [], current = 0, label = 'Progress', className = '' }) {
  return <ol className={cx('mv-stepper', className)} aria-label={label}>{steps.map((s, i) => {
    const st = i < current ? 'done' : i === current ? 'current' : 'todo';
    return <li key={i} className={cx('mv-step', `mv-step--${st}`)} aria-current={st === 'current' ? 'step' : undefined}>
      <span className="mv-step-dot" aria-hidden="true">{st === 'done' ? <Svg d={ICONS.check} sw={2.4} /> : i + 1}</span>
      <span className="mv-step-label">{s}<span className="mv-sr">{st === 'done' ? ' (done)' : st === 'todo' ? ' (not started)' : ''}</span></span></li>;
  })}</ol>;
}

/* ================= Batch 4: specialised fields, Ask conversation, charts ================= */

/* ---------------- FileField (image upload) ---------------- */
const fmtBytes = (n) => n >= 1048576 ? `${(n / 1048576).toFixed(n % 1048576 ? 1 : 0)} MB` : `${Math.round(n / 1024)} KB`;
function FileField({
  label, hint, error, accept = 'image/png,image/jpeg,image/webp', maxBytes = 5 * 1048576, value, onSelect, onRemove,
  removeLabel = 'Remove', chooseLabel = 'Choose an image', replaceLabel = 'Replace', busy = false, busyLabel = 'Preparing…',
  disabled = false, required = false, optional = false, typesText, emptyText = 'Nothing chosen', id, className = '', style = {},
}) {
  const auto = useId();
  const fid = id || `mv-ff${auto.replace(/:/g, '')}`;
  const inputRef = useRef(null);
  const [over, setOver] = React.useState(false);
  const [localError, setLocalError] = React.useState(null);
  const err = error || localError;
  const NAMES = { png: 'PNG', jpeg: 'JPEG', jpg: 'JPEG', webp: 'WebP', gif: 'GIF', 'svg+xml': 'SVG', pdf: 'PDF' };
  const tl = accept.split(',').map((t) => NAMES[t.trim().split('/')[1]] || t.trim().split('/')[1].toUpperCase());
  const types = typesText || (tl.length > 1 ? `${tl.slice(0, -1).join(', ')} or ${tl[tl.length - 1]}` : tl[0]);
  const hintText = hint || `${types}, up to ${fmtBytes(maxBytes)}.`;
  const take = (file) => {
    if (!file) return;
    const ok = accept.split(',').some((t) => t.trim() === file.type || (t.endsWith('/*') && file.type.startsWith(t.slice(0, -1))));
    if (!ok) { setLocalError(`Choose a ${types} file.`); return; }
    if (file.size > maxBytes) { setLocalError(`That file is ${fmtBytes(file.size)}. The limit is ${fmtBytes(maxBytes)}.`); return; }
    setLocalError(null); onSelect && onSelect(file);
  };
  const inert = disabled || busy;
  const hintId = `${fid}-hint`, msgId = `${fid}-msg`;
  return (
    <div className={cx('mv-field', 'mv-file', err && 'mv-field--error', disabled && 'mv-field--disabled', className)} style={style}>
      {label && <label className="mv-label" htmlFor={fid}>{label}{optional && !required && <span className="opt">(optional)</span>}</label>}
      <div className={cx('mv-file-zone', over && 'is-over', value && 'has-value')}
        onDragOver={(e) => { if (inert) return; e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { if (inert) return; e.preventDefault(); setOver(false); take(e.dataTransfer.files && e.dataTransfer.files[0]); }}>
        <div className="mv-file-thumb" aria-hidden={!value || undefined}>
          {value && value.src ? <img src={value.src} alt={value.alt || ''} /> : <Svg d={['M4 5h16v14H4z', 'M4 15l4.5-4.5L13 15l2.5-2.5L20 17', 'M15.5 9.5h.01']} />}
        </div>
        <div className="mv-file-text">
          <div className="mv-file-name">{busy ? busyLabel : value ? (value.name || 'Image chosen') : emptyText}</div>
          <div className="mv-file-meta">{value && value.meta ? value.meta : 'Drop an image here, or choose one.'}</div>
        </div>
        <div className="mv-file-acts">
          <input ref={inputRef} id={fid} className="mv-sr mv-file-input" type="file" accept={accept} disabled={inert} required={required}
            aria-invalid={err ? true : undefined} aria-describedby={[err ? msgId : null, hintId].filter(Boolean).join(' ')}
            onChange={(e) => { take(e.target.files && e.target.files[0]); e.target.value = ''; }} />
          <Button size="sm" variant="ghost" busy={busy} busyLabel={busyLabel} disabled={disabled} onClick={() => inputRef.current && inputRef.current.click()}
            aria-describedby={hintId}>{value ? replaceLabel : chooseLabel}</Button>
          {value && onRemove && <Button size="sm" variant="ghost" disabled={inert} onClick={onRemove}>{removeLabel}</Button>}
        </div>
      </div>
      {err && <div id={msgId} className="mv-msg mv-msg--error" role="alert"><Svg d={ICONS.alert} sw={2} /><span>{err}</span></div>}
      <div id={hintId} className="mv-hint">{hintText}</div>
    </div>
  );
}

/* ---------------- ColorField ---------------- */
const HEX6 = /^#[0-9a-f]{6}$/i;
const normHex = (v) => {
  let x = String(v || '').trim(); if (x && x[0] !== '#') x = '#' + x;
  if (/^#[0-9a-f]{3}$/i.test(x)) x = '#' + x.slice(1).split('').map((c) => c + c).join('');
  return x.toUpperCase();
};
function ColorField({ label, value = '#B68A3E', onChange, hint, error, disabled = false, readOnly = false, id, children,
  invalidText = 'Use a six-digit hex value such as #1D4ED8.', className = '', style = {} }) {
  const auto = useId();
  const fid = id || `mv-cf${auto.replace(/:/g, '')}`;
  const [draft, setDraft] = React.useState(value);
  const [bad, setBad] = React.useState(false);
  useEffect(() => { setDraft(value); setBad(false); }, [value]);
  const commit = (raw) => { const v = normHex(raw); if (HEX6.test(v)) { setBad(false); setDraft(v); if (v !== value) onChange && onChange(v); } else setBad(true); };
  const err = error || (bad ? invalidText : null);
  const hintId = hint ? `${fid}-hint` : undefined, msgId = `${fid}-msg`, noteId = children ? `${fid}-note` : undefined;
  const swatch = HEX6.test(normHex(draft)) ? normHex(draft) : value;
  return (
    <div className={cx('mv-field', 'mv-color', err && 'mv-field--error', disabled && 'mv-field--disabled', readOnly && 'mv-field--readonly', className)} style={style}>
      {label && <label className="mv-label" htmlFor={fid}>{label}</label>}
      <div className="mv-color-row">
        <label className="mv-color-swatch" style={{ '--sw': swatch }}>
          <input type="color" value={HEX6.test(value) ? value.toLowerCase() : '#000000'} disabled={disabled || readOnly}
            aria-label={`${typeof label === 'string' ? label : 'Colour'} — picker`} onChange={(e) => commit(e.target.value)} />
        </label>
        <div className="mv-control">
          <input id={fid} className="mv-input mv-mono" value={draft} disabled={disabled} readOnly={readOnly} spellCheck={false} autoCapitalize="none"
            maxLength={7} inputMode="text" aria-invalid={err ? true : undefined}
            aria-describedby={[err ? msgId : null, noteId, hintId].filter(Boolean).join(' ') || undefined}
            onChange={(e) => { setDraft(e.target.value); if (HEX6.test(normHex(e.target.value))) commit(e.target.value); }}
            onBlur={(e) => commit(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') commit(e.currentTarget.value); }} />
        </div>
      </div>
      {err && <div id={msgId} className="mv-msg mv-msg--error" role="alert"><Svg d={ICONS.alert} sw={2} /><span>{err}</span></div>}
      {children && <div id={noteId} className="mv-color-note" aria-live="polite">{children}</div>}
      {hint && <div id={hintId} className="mv-hint">{hint}</div>}
    </div>
  );
}

/* ================= Ask conversation ================= */
function Thread({ children, label = 'Conversation with Marva', busy = false, className = '', threadRef }) {
  return <div ref={threadRef} className={cx('mv-thread', className)} role="log" aria-label={label} aria-busy={busy || undefined}>{children}</div>;
}
function Message({ from = 'marva', children, pending = false, streaming = false, failed = false, meta, className = '' }) {
  const me = from === 'user';
  if (pending) {
    return <div className={cx('mv-msg-row', 'mv-msg-row--marva', className)}>
      <Orb size={22} /><div className="mv-bubble mv-bubble--marva mv-typing" role="status"><span className="mv-sr">Marva is thinking</span><i /><i /><i /></div></div>;
  }
  return (
    <div className={cx('mv-msg-row', me ? 'mv-msg-row--user' : 'mv-msg-row--marva', failed && 'is-failed', className)}>
      {!me && <Orb size={22} />}
      <div className="mv-msg-col">
        <div className={cx('mv-bubble', me ? 'mv-bubble--user' : 'mv-bubble--marva')} aria-busy={streaming || undefined}>
          <span className="mv-sr">{me ? 'You: ' : 'Marva: '}</span>
          {me ? <div className="mv-bubble-plain">{children}</div> : <div className="mv-prose mv-prose--chat">{children}</div>}
          {streaming && <span className="mv-caret" aria-hidden="true" />}
        </div>
        {meta && <div className="mv-msg-meta">{meta}</div>}
      </div>
    </div>
  );
}
/* What a turn attached: source, proposal and output cards, indented to the bubble's edge. */
function MessageAttachments({ children, prior = false, label, className = '' }) {
  return <div className={cx('mv-attach', prior && 'mv-attach--prior', className)}>{label && <p className="mv-attach-label">{label}</p>}{children}</div>;
}
function ThreadNotice({ tone = 'danger', children, onRetry, retryLabel = 'Try again', busy = false, className = '' }) {
  return (
    <div className={cx('mv-tnotice', `mv-tnotice--${tone}`, className)} role={tone === 'danger' ? 'alert' : 'status'}>
      <Svg d={tone === 'danger' ? TONE_ICON.danger : TONE_ICON.info} sw={2} />
      <span className="mv-tnotice-text">{children}</span>
      {onRetry && <Button size="sm" variant="ghost" busy={busy} busyLabel="Trying again…" onClick={onRetry}>{retryLabel}</Button>}
    </div>
  );
}
/* `placement="page"`: on a page the group sits ON the content column — no inset, so its label
   and items align with the blocks above and below (MAR-381). The dock's default keeps its
   padded, filled-on-hover rows. */
/* `layout="row"` (MAR-401): the label and the questions on one wrapping line, each question a
   pill — the page's "Or ask her" row as Knowledge draws it, under the content it follows. */
function Suggestions({ items = [], label = 'Try asking', onPick, disabled = false, placement = 'dock', layout = 'list', className = '' }) {
  const id = useId();
  return (
    <div className={cx('mv-suggest', placement === 'page' && 'mv-suggest--page', layout === 'row' && 'mv-suggest--row', className)} role="group" aria-labelledby={`${id}-l`}>
      <div id={`${id}-l`} className="mv-suggest-label">{label}</div>
      {items.map((s) => {
        const it = typeof s === 'string' ? { label: s } : s;
        return <button key={it.label} type="button" className="mv-suggest-item" disabled={disabled} onClick={() => onPick && onPick(it.label)}>
          <Svg d={['M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14z', 'M21 21l-5-5']} /><span>{it.label}</span></button>;
      })}
    </div>
  );
}

const COUNT_FMT = (n) => n.toLocaleString('en-GB');
function AskComposer({ value = '', onChange, onSubmit, placeholder = 'Ask about a customer, a product, a number…', label = 'Ask Marva',
  maxChars = 4000, countFrom = 3500, busy = false, disabled = false, clipped: clippedProp, clippedNote, maxRows = 6, inputRef, className = '' }) {
  const ref = useRef(null);
  const setRef = (el) => { ref.current = el; if (typeof inputRef === 'function') inputRef(el); else if (inputRef) inputRef.current = el; };
  const [clippedState, setClipped] = React.useState(false);
  const clipped = clippedProp !== undefined ? clippedProp : clippedState;
  const composing = useRef(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    el.style.height = 'auto';
    const lh = parseFloat(getComputedStyle(el).lineHeight) || 20;
    el.style.height = Math.min(el.scrollHeight, lh * maxRows + 16) + 'px';
  }, [value, maxRows]);
  const set = (v) => { if (v.length > maxChars) { onChange && onChange(v.slice(0, maxChars)); setClipped(true); } else { onChange && onChange(v); if (v.length < maxChars) setClipped(false); } };
  const can = value.trim().length > 0 && !busy && !disabled && value.length <= maxChars;
  const send = () => { if (can) { onSubmit && onSubmit(value); setClipped(false); } };
  const showCount = value.length >= countFrom;
  const id = useId();
  return (
    <div className={cx('mv-composer', disabled && 'is-disabled', className)}>
      <div className="mv-composer-bar">
        <textarea ref={setRef} rows={1} className="mv-composer-input" aria-label={label} placeholder={placeholder} value={value} disabled={disabled}
          aria-describedby={showCount || clipped ? `${id}-lim` : undefined}
          onCompositionStart={() => { composing.current = true; }} onCompositionEnd={() => { composing.current = false; }}
          onChange={(e) => set(e.target.value)}
          onPaste={(e) => {
            const t = e.clipboardData.getData('text'); const el = e.currentTarget;
            const next = value.slice(0, el.selectionStart ?? value.length) + t + value.slice(el.selectionEnd ?? value.length);
            if (next.length <= maxChars) return; e.preventDefault(); set(next);
          }}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !composing.current && !e.nativeEvent.isComposing) { e.preventDefault(); send(); } }} />
        <button type="button" className="mv-composer-send" aria-label={busy ? 'Marva is answering' : 'Ask her'} title="Ask her (Enter)"
          disabled={!can} onClick={send}>
          {busy ? <span className="mv-spin" aria-hidden="true" /> : <Svg d={['M12 19V5', 'M6 11l6-6 6 6']} sw={2} />}
        </button>
      </div>
      {(showCount || clipped) && <div id={`${id}-lim`} className="mv-composer-limit" aria-live="polite">
        {clipped && <span className="note">{clippedNote || `Your paste was cut to ${COUNT_FMT(maxChars)} characters, the most Marva can read at once.`}</span>}
        {showCount && <span className={cx('count', value.length >= maxChars && 'at')}>{COUNT_FMT(value.length)} / {COUNT_FMT(maxChars)}</span>}
      </div>}
      <div className="mv-composer-hint">Enter to ask · Shift+Enter for a new line</div>
    </div>
  );
}

const SOURCE_COPY = {
  partial: 'Partial: some of this could not be read',
  unavailable: 'Unavailable: Marva could not read this just now',
  not_found: 'Not found: Marva could not find this record in your store',
  invalid: 'Not read: the question did not fit what this can look up',
  refused: 'Not read: this is not something Marva can read for you here',
  cancelled: 'Not read: the answer ran out of time before this was read',
};
function SourceCard({ title, status = 'ok', recordId, link, statusText, children, dropped = false, className = '' }) {
  const read = status === 'ok' || status === 'partial';
  const line = status === 'ok' ? null : statusText || SOURCE_COPY[status] || 'Not read: this could not be read';
  const L = link && (link.as || 'a');
  return (
    <div className={cx('mv-src', `mv-src--${read ? (status === 'partial' ? 'partial' : 'ok') : 'unread'}`, className)} data-status={status}>
      <div className="mv-src-head">
        <span className="mv-src-title">{title}</span>
        {link && <L className="mv-src-link" href={link.href} to={link.to} onClick={link.onClick}>Open {link.label}</L>}
      </div>
      {recordId && <div className="mv-src-id">{recordId}</div>}
      {line && <p className="mv-src-status" role="note">{!read && <Svg d={TONE_ICON.info} sw={2} />}{line}</p>}
      {read && children && <div className="mv-src-body">{children}</div>}
      {read && !children && dropped && <p className="mv-src-status" role="note">The detail was too large to keep with this thread.</p>}
    </div>
  );
}
function SourceCardList({ children, label, prior = false, className = '' }) {
  return <div className={cx('mv-srclist', prior && 'mv-srclist--prior', className)}>{label && <p className="mv-srclist-label">{label}</p>}{children}</div>;
}

const PROPOSAL_STATE = {
  live: { kicker: 'Needs your confirmation' }, retry: { kicker: 'Not confirmed yet' },
  expired: { kicker: 'Expired' }, cancelled: { kicker: 'Cancelled' }, refused: { kicker: 'Not done' },
  inert: { kicker: 'Prepared earlier' }, applied: { kicker: 'Done' },
};
function ProposalCard({ state = 'live', summary, diff = [], expiry, message, onConfirm, onCancel, working = false, canAct = true,
  confirmLabel = 'Confirm', cancelLabel = 'Cancel', receipt, className = '' }) {
  const meta = PROPOSAL_STATE[state] || PROPOSAL_STATE.live;
  const controls = state === 'live' || state === 'retry' || state === 'expired';
  const off = state === 'expired' || !canAct;
  const msgRole = state === 'refused' || state === 'retry' ? 'alert' : state === 'cancelled' || state === 'applied' ? 'status' : 'note';
  const id = useId();
  return (
    <div className={cx('mv-prop', `mv-prop--${state}`, className)} role="group" aria-labelledby={`${id}-k ${id}-t`} aria-busy={working || undefined}>
      <div className="mv-prop-kicker" id={`${id}-k`}>
        {state === 'applied' ? <Svg d={ICONS.check} sw={2.2} /> : state === 'live' || state === 'retry' ? <span className="mv-prop-dot" aria-hidden="true" /> : null}
        {meta.kicker}
      </div>
      <div className="mv-prop-title" id={`${id}-t`}>{state === 'applied' && receipt ? receipt.what : summary}</div>
      {state !== 'applied' && diff.length > 0 && <dl className="mv-prop-diff">{diff.map((d) => <React.Fragment key={d.label}>
        <dt>{d.label}</dt>
        <dd className={d.pre ? 'pre' : undefined}>{d.from !== undefined ? <><span className="from">{d.from}</span><span aria-hidden="true"> → </span><span className="mv-sr"> to </span><span className="to">{d.to}</span></> : d.value}</dd>
      </React.Fragment>)}</dl>}
      {state === 'applied' && receipt && <div className="mv-prop-receipt">
        <span className="mv-mono">{receipt.id}{receipt.at ? ` · ${receipt.at}` : ''}</span>
        {receipt.link && React.createElement(receipt.link.as || 'a', { className: 'mv-src-link', href: receipt.link.href, to: receipt.link.to }, receipt.link.label || 'See it in the Ledger')}
      </div>}
      {(state === 'live' || state === 'retry') && expiry && <p className="mv-prop-expiry">{expiry}</p>}
      {message && <p className="mv-prop-msg" role={msgRole}>{message}</p>}
      {controls && <div className="mv-prop-act">
        <Button size="sm" variant="gold" disabled={off} busy={working} busyLabel="Confirming…" onClick={onConfirm}>{state === 'retry' ? 'Confirm again' : confirmLabel}</Button>
        <Button size="sm" variant="ghost" disabled={off || working} onClick={onCancel}>{cancelLabel}</Button>
      </div>}
    </div>
  );
}

/* ================= Charts ================= */
const SERIES_VARS = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)', 'var(--chart-5)'];
const niceStep = (span, n = 4) => { const raw = span / n; const p = Math.pow(10, Math.floor(Math.log10(raw || 1))); const m = raw / p; return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 2.5 ? 2.5 : m <= 5 ? 5 : 10) * p; };
const compact = (v) => { const a = Math.abs(v); if (a >= 1e6) return (v / 1e6).toFixed(a >= 1e7 ? 0 : 1).replace(/\.0$/, '') + 'M'; if (a >= 1e3) return (v / 1e3).toFixed(a >= 1e4 ? 0 : 1).replace(/\.0$/, '') + 'K'; return String(+v.toFixed(2)); };
function useWidth(ref, fallback = 520) {
  const [w, setW] = React.useState(fallback);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const set = () => setW(Math.max(200, el.clientWidth));
    set(); if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(set); ro.observe(el); return () => ro.disconnect();
  }, []);
  return w;
}
const fin = (v) => typeof v === 'number' && Number.isFinite(v);

function ChartTable({ labels, series, fmt, partial, caption }) {
  return (
    <div className="mv-chart-tablewrap"><table className="mv-chart-table"><caption className="mv-sr">{caption}</caption>
      <thead><tr><th scope="col">{''}</th>{series.map((s) => <th key={s.label} scope="col">{s.label}</th>)}</tr></thead>
      <tbody>{labels.map((l, i) => <tr key={l}><th scope="row">{l}{partial[i] ? ' (partial)' : ''}</th>
        {series.map((s) => <td key={s.label}>{fin(s.data[i]) ? fmt(s.data[i]) : 'No data'}</td>)}</tr>)}</tbody></table></div>
  );
}

function Chart({ type = 'line', title, description, labels = [], series = [], partial: partialProp, format, prefix = '', suffix = '',
  stacked = false, height = 240, state = 'ready', onRetry, errorText, emptyText = 'Nothing to chart for this period yet.',
  legend, table = 'toggle', defaultView = 'chart', className = '' }) {
  const wrapRef = useRef(null);
  const width = useWidth(wrapRef);
  const [view, setView] = React.useState(defaultView);
  const [hi, setHi] = React.useState(null);
  const id = useId().replace(/:/g, '');
  const fmt = format || ((v) => `${prefix}${Number(v).toLocaleString()}${suffix}`);
  const S = series.slice(0, 5).concat(series.length > 5 ? [{ label: 'Other', other: true, data: labels.map((_, i) => series.slice(5).reduce((a, s) => a + (fin(s.data[i]) ? s.data[i] : 0), 0)) }] : []);
  const partial = labels.map((_, i) => (partialProp && partialProp[i]) || series.some((s) => s.partial && s.partial[i]));
  const gap = S.some((s) => labels.some((_, i) => !fin(s.data[i])));
  const doStack = stacked && !gap && type !== 'line' && type !== 'pie';
  const color = (s, i) => (s.other ? 'var(--chart-other)' : SERIES_VARS[i % 5]);
  const showLegend = legend !== undefined ? legend : (type === 'pie' ? true : S.length > 1);
  const head = (title || description || table !== false) && (
    <div className="mv-chart-head">
      <div>{title && <div className="mv-chart-title" id={`${id}-t`}>{title}</div>}{description && <div className="mv-chart-desc">{description}</div>}</div>
      {table === 'toggle' && state === 'ready' && labels.length > 0 && <button type="button" className="mv-chart-view" aria-pressed={view === 'table'}
        onClick={() => setView(view === 'table' ? 'chart' : 'table')}>{view === 'table' ? 'Show chart' : 'Show table'}</button>}
    </div>
  );
  let body;
  if (state === 'loading') body = <div className="mv-chart-state" style={{ height }}><LoadingState caption="Reading the numbers…" /></div>;
  else if (state === 'error') body = <div className="mv-chart-state" style={{ height }}><ErrorState title={errorText || "Couldn't load this chart."} onRetry={onRetry}>Nothing here means zero — the read didn't finish.</ErrorState></div>;
  else if (!labels.length || !S.length || S.every((s) => s.data.every((v) => !fin(v)))) body = <div className="mv-chart-state" style={{ height }}><EmptyState size="sm" title={emptyText} /></div>;
  else if (view === 'table') body = <ChartTable labels={labels} series={S} fmt={fmt} partial={partial} caption={title || 'Chart data'} />;
  else if (type === 'pie') body = <Donut labels={labels} s={S[0]} fmt={fmt} height={height} width={width} id={id} title={title} />;
  else {
    const padL = 44, padR = 12, padT = 10, padB = 26;
    const iw = width - padL - padR, ih = height - padT - padB;
    const vals = [];
    labels.forEach((_, i) => { if (doStack) vals.push(S.reduce((a, s) => a + (fin(s.data[i]) ? s.data[i] : 0), 0)); else S.forEach((s) => fin(s.data[i]) && vals.push(s.data[i])); });
    const minV = Math.min(0, ...vals), maxV = Math.max(...vals, 0) || 1;
    const step = niceStep(maxV - minV); const top = Math.ceil(maxV / step) * step; const bot = Math.floor(minV / step) * step;
    const y = (v) => padT + ih - ((v - bot) / (top - bot || 1)) * ih;
    const band = iw / labels.length;
    const cxAt = (i) => type === 'bar' ? padL + band * i + band / 2 : padL + (labels.length === 1 ? iw / 2 : (iw * i) / (labels.length - 1));
    const ticks = []; for (let t = bot; t <= top + 1e-9; t += step) ticks.push(+t.toFixed(6));
    const every = Math.max(1, Math.ceil(labels.length / Math.max(2, Math.floor(iw / 64))));
    const segs = (s) => { const out = []; let cur = []; labels.forEach((_, i) => { if (fin(s.data[i])) cur.push([cxAt(i), y(s.data[i])]); else { if (cur.length) out.push(cur); cur = []; } }); if (cur.length) out.push(cur); return out; };
    const marks = [];
    if (type === 'bar') {
      const groups = doStack ? 1 : S.length; const bw = Math.min(24, (band * 0.72 - (groups - 1) * 2) / groups);
      labels.forEach((_, i) => {
        let acc = 0;
        S.forEach((s, k) => {
          const v = s.data[i]; if (!fin(v)) return;
          const x = doStack ? cxAt(i) - bw / 2 : cxAt(i) - (groups * bw + (groups - 1) * 2) / 2 + k * (bw + 2);
          const y0 = y(doStack ? acc : 0), y1 = y(doStack ? acc + v : v); acc += doStack ? v : 0;
          const h = Math.max(1, Math.abs(y0 - y1)) - (doStack && k > 0 ? 2 : 0); const yy = Math.min(y0, y1);
          const isTop = !doStack || k === S.length - 1;
          const r = isTop ? Math.min(4, bw / 2, h) : 0;
          const d = `M${x},${yy + h}V${yy + r}${r ? `Q${x},${yy} ${x + r},${yy}` : ''}H${x + bw - r}${r ? `Q${x + bw},${yy} ${x + bw},${yy + r}` : ''}V${yy + h}Z`;
          marks.push(<path key={`${i}-${k}`} d={d} fill={partial[i] ? `url(#${id}-h${k})` : color(s, k)} stroke={partial[i] ? color(s, k) : 'none'} strokeWidth={partial[i] ? 1.5 : 0} strokeDasharray={partial[i] ? '3 2' : undefined} />);
        });
      });
    } else {
      S.forEach((s, k) => {
        const c = color(s, k);
        segs(s).forEach((pts, j) => {
          const d = 'M' + pts.map((p) => p.join(',')).join('L');
          if (type === 'area') marks.push(<path key={`a${k}-${j}`} d={`${d}L${pts[pts.length - 1][0]},${y(0)}L${pts[0][0]},${y(0)}Z`} fill={c} fillOpacity={0.12} />);
          marks.push(<path key={`l${k}-${j}`} d={d} fill="none" stroke={c} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />);
        });
        labels.forEach((_, i) => {
          if (!fin(s.data[i])) return;
          const lone = !fin(s.data[i - 1]) && !fin(s.data[i + 1]);
          if (partial[i]) marks.push(<circle key={`p${k}-${i}`} cx={cxAt(i)} cy={y(s.data[i])} r={4} fill="var(--surface)" stroke={c} strokeWidth={1.75} strokeDasharray="2.5 1.5" />);
          else if (lone || hi === i) marks.push(<circle key={`d${k}-${i}`} cx={cxAt(i)} cy={y(s.data[i])} r={hi === i ? 5 : 4} fill={c} stroke="var(--surface)" strokeWidth={2} />);
        });
      });
    }
    const pick = (clientX) => { const r = wrapRef.current.getBoundingClientRect(); const x = clientX - r.left; let i = type === 'bar' ? Math.floor((x - padL) / band) : Math.round(((x - padL) / iw) * (labels.length - 1)); i = Math.max(0, Math.min(labels.length - 1, i)); setHi(i); };
    const tipX = hi != null ? cxAt(hi) : 0;
    body = (
      <div className="mv-chart-plot" style={{ height }}>
        <svg width={width} height={height} role="img" aria-labelledby={title ? `${id}-t ${id}-sum` : `${id}-sum`} focusable="false">
          <defs>{S.map((s, k) => <pattern key={k} id={`${id}-h${k}`} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="6" height="6" fill="var(--surface)" /><rect width="2.5" height="6" fill={color(s, k)} /></pattern>)}</defs>
          {ticks.map((t) => <g key={t}><line x1={padL} x2={width - padR} y1={y(t)} y2={y(t)} stroke={t === 0 ? 'var(--line)' : 'var(--chart-grid)'} strokeWidth={1} />
            <text x={padL - 8} y={y(t)} dy="0.32em" textAnchor="end" className="mv-chart-tick">{compact(t)}</text></g>)}
          {labels.map((l, i) => i % every === 0 && <text key={l} x={cxAt(i)} y={height - 8} textAnchor="middle" className="mv-chart-tick">{l}</text>)}
          {hi != null && type !== 'bar' && <line x1={tipX} x2={tipX} y1={padT} y2={padT + ih} stroke="var(--ink-soft)" strokeWidth={1} strokeDasharray="2 3" />}
          {hi != null && type === 'bar' && <rect x={padL + band * hi} y={padT} width={band} height={ih} fill="var(--ink)" fillOpacity={0.04} />}
          {marks}
        </svg>
        <div className="mv-chart-hit" tabIndex={0} role="application" aria-roledescription="chart" aria-label={`${title || 'Chart'}: use the left and right arrow keys to read each ${labels.length > 1 ? 'point' : 'value'}`}
          onMouseMove={(e) => pick(e.clientX)} onMouseLeave={() => setHi(null)} onFocus={() => setHi(hi ?? labels.length - 1)} onBlur={() => setHi(null)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); setHi((h) => Math.max(0, Math.min(labels.length - 1, (h ?? 0) + (e.key === 'ArrowRight' ? 1 : -1)))); }
            else if (e.key === 'Home') { e.preventDefault(); setHi(0); } else if (e.key === 'End') { e.preventDefault(); setHi(labels.length - 1); } else if (e.key === 'Escape') setHi(null);
          }} />
        {hi != null && <div className="mv-chart-tip" role="status" style={{ left: Math.min(Math.max(tipX, 90), width - 90), top: padT }}>
          <div className="mv-chart-tip-h">{labels[hi]}{partial[hi] && <span className="mv-chart-tip-p"> · partial</span>}</div>
          {S.map((s, k) => <div key={s.label} className="mv-chart-tip-r"><span className="sw" style={{ background: color(s, k) }} aria-hidden="true" /><span className="l">{s.label}</span><span className="v">{fin(s.data[hi]) ? fmt(s.data[hi]) : 'No data'}</span></div>)}
        </div>}
        <p className="mv-sr" id={`${id}-sum`}>{summarise(type, labels, S, fmt)}</p>
      </div>
    );
  }
  const partLabels = labels.filter((_, i) => partial[i]);
  return (
    <figure className={cx('mv-chart', className)} ref={wrapRef}>
      {head}
      {body}
      {state === 'ready' && view === 'chart' && showLegend && type !== 'pie' && <ul className="mv-chart-legend">{S.map((s, k) =>
        <li key={s.label}><span className={cx('sw', type !== 'bar' && 'sw--line')} style={{ background: color(s, k) }} aria-hidden="true" />{s.label}</li>)}</ul>}
      {state === 'ready' && (partLabels.length > 0 || (gap && type !== 'pie') || (stacked && gap)) && <figcaption className="mv-chart-notes">
        {partLabels.length > 0 && <span>{partLabels.length === 1 ? 'Partial period' : 'Partial periods'}: {partLabels.join(', ')} — the window covers only part of {partLabels.length === 1 ? 'it' : 'them'}, so {partLabels.length === 1 ? 'it reads' : 'they read'} low. </span>}
        {gap && type !== 'pie' && <span>Gaps are periods with no data, not zero. </span>}
        {stacked && gap && <span>Shown unstacked because a series has gaps.</span>}
      </figcaption>}
    </figure>
  );
}
function summarise(type, labels, S, fmt) {
  if (!labels.length) return '';
  return S.map((s) => {
    const pts = labels.map((l, i) => [l, s.data[i]]).filter(([, v]) => fin(v)); if (!pts.length) return `${s.label}: no data.`;
    const mx = pts.reduce((a, b) => (b[1] > a[1] ? b : a)), mn = pts.reduce((a, b) => (b[1] < a[1] ? b : a));
    return `${s.label}: ${pts.length} of ${labels.length} periods, from ${fmt(pts[0][1])} (${pts[0][0]}) to ${fmt(pts[pts.length - 1][1])} (${pts[pts.length - 1][0]}); high ${fmt(mx[1])} in ${mx[0]}, low ${fmt(mn[1])} in ${mn[0]}.`;
  }).join(' ') + ' Show table for every value.';
}
function Donut({ labels, s, fmt, height, width, id, title }) {
  const rows = labels.map((l, i) => ({ l, v: s.data[i], i })).filter((r) => fin(r.v) && r.v > 0);
  const total = rows.reduce((a, r) => a + r.v, 0);
  const shown = rows.length > 5 ? rows.slice(0, 4).concat([{ l: 'Other', v: rows.slice(4).reduce((a, r) => a + r.v, 0), other: true }]) : rows;
  const R = Math.min(height, width) / 2 - 8, r0 = R * 0.62, cx0 = Math.min(width / 2, R + 8), cy0 = height / 2;
  let a = -Math.PI / 2; const gapA = shown.length > 1 ? 2 / R : 0;
  const [hi, setHi] = React.useState(null);
  const arcs = shown.map((r, k) => {
    const span = (r.v / total) * Math.PI * 2; const a0 = a + gapA / 2, a1 = a + span - gapA / 2; a += span;
    const p = (ang, rad) => [cx0 + rad * Math.cos(ang), cy0 + rad * Math.sin(ang)]; const big = a1 - a0 > Math.PI ? 1 : 0;
    const [x0, y0] = p(a0, R), [x1, y1] = p(a1, R), [x2, y2] = p(a1, r0), [x3, y3] = p(a0, r0);
    const fill = r.other ? 'var(--chart-other)' : SERIES_VARS[k % 5];
    return <path key={r.l} d={shown.length === 1 ? `M${cx0 - R},${cy0}a${R},${R} 0 1,0 ${2 * R},0a${R},${R} 0 1,0 ${-2 * R},0M${cx0 - r0},${cy0}a${r0},${r0} 0 1,1 ${2 * r0},0a${r0},${r0} 0 1,1 ${-2 * r0},0` : `M${x0},${y0}A${R},${R} 0 ${big} 1 ${x1},${y1}L${x2},${y2}A${r0},${r0} 0 ${big} 0 ${x3},${y3}Z`}
      fill={fill} fillRule="evenodd" opacity={hi == null || hi === k ? 1 : 0.45} onMouseEnter={() => setHi(k)} onMouseLeave={() => setHi(null)} />;
  });
  const pct = (v) => `${Math.round((v / total) * 100)}%`;
  return (
    <div className="mv-donut" style={{ minHeight: height }}>
      <svg width={cx0 * 2} height={height} role="img" aria-labelledby={`${id}-ds`} focusable="false">{arcs}
        <text x={cx0} y={cy0 - 4} textAnchor="middle" className="mv-donut-total">{fmt(total)}</text>
        <text x={cx0} y={cy0 + 14} textAnchor="middle" className="mv-chart-tick">total</text></svg>
      <ul className="mv-donut-legend">{shown.map((r, k) => <li key={r.l} onMouseEnter={() => setHi(k)} onMouseLeave={() => setHi(null)} className={hi === k ? 'on' : undefined}>
        <span className="sw" style={{ background: r.other ? 'var(--chart-other)' : SERIES_VARS[k % 5] }} aria-hidden="true" />
        <span className="l">{r.l}</span><span className="v">{fmt(r.v)}</span><span className="p">{pct(r.v)}</span></li>)}</ul>
      <p className="mv-sr" id={`${id}-ds`}>{`${title || 'Share'}: ` + shown.map((r) => `${r.l} ${fmt(r.v)} (${pct(r.v)})`).join(', ') + '.'}</p>
    </div>
  );
}

function Sparkline({ data = [], label, partial = [], width = 120, height = 32, type = 'line', valueText, className = '' }) {
  const vals = data.filter(fin); if (!vals.length) return <span className={cx('mv-spark', 'mv-spark--empty', className)} role="img" aria-label={`${label}: no data`}>—</span>;
  const mn = Math.min(...vals, type === 'bar' ? 0 : Infinity), mx = Math.max(...vals); const span = mx - mn || 1;
  const x = (i) => 2 + (data.length === 1 ? (width - 4) / 2 : ((width - 4) * i) / (data.length - 1));
  const y = (v) => height - 3 - ((v - mn) / span) * (height - 6);
  const last = [...data.keys()].reverse().find((i) => fin(data[i]));
  const desc = valueText || `${label}: ${vals.length} points, latest ${data[last]}, high ${mx}, low ${Math.min(...vals)}`;
  let marks;
  if (type === 'bar') {
    const bw = Math.max(2, (width - 4) / data.length - 2);
    marks = data.map((v, i) => fin(v) && <rect key={i} x={2 + i * ((width - 4) / data.length)} y={y(v)} width={bw} height={Math.max(1, height - 3 - y(v))} rx={1.5}
      fill={i === last ? 'var(--chart-1)' : 'var(--chart-other)'} opacity={partial[i] ? 0.45 : 1} />);
  } else {
    const segs = []; let cur = []; data.forEach((v, i) => { if (fin(v)) cur.push(`${x(i)},${y(v)}`); else { if (cur.length) segs.push(cur); cur = []; } }); if (cur.length) segs.push(cur);
    marks = <>{segs.map((s, i) => <path key={i} d={'M' + s.join('L')} fill="none" stroke="var(--chart-1)" strokeWidth={1.75} strokeLinejoin="round" strokeLinecap="round" />)}
      <circle cx={x(last)} cy={y(data[last])} r={3} fill={partial[last] ? 'var(--surface)' : 'var(--chart-1)'} stroke={partial[last] ? 'var(--chart-1)' : 'var(--surface)'} strokeWidth={partial[last] ? 1.5 : 1.5} /></>;
  }
  return <svg className={cx('mv-spark', className)} width={width} height={height} role="img" aria-label={desc} focusable="false">{marks}</svg>;
}

function Metric({ label, value, delta, deltaText, goodWhen = 'up', period, spark, sparkPartial, size = 'md', className = '' }) {
  const dir = delta == null || delta === 0 ? 'flat' : delta > 0 ? 'up' : 'down';
  const good = dir === 'flat' ? 'neutral' : (dir === goodWhen ? 'good' : 'bad');
  const word = dir === 'up' ? 'up' : dir === 'down' ? 'down' : 'no change';
  return (
    <div className={cx('mv-metric', size === 'sm' && 'mv-metric--sm', className)}>
      <div className="mv-metric-label">{label}</div>
      <div className="mv-metric-row">
        <span className="mv-metric-value">{value}</span>
        {delta != null && <span className={cx('mv-metric-delta', `is-${good}`)}>
          <Svg d={dir === 'up' ? 'M12 19V5M6 11l6-6 6 6' : dir === 'down' ? 'M12 5v14M6 13l6 6 6-6' : 'M5 12h14'} sw={2} />
          <span className="mv-sr">{word} </span>{deltaText || `${Math.abs(delta).toFixed(1)}%`}</span>}
      </div>
      {period && <div className="mv-metric-period">{period}</div>}
      {spark && <Sparkline data={spark} partial={sparkPartial} label={`${label} trend`} width={140} height={28} />}
    </div>
  );
}

export {
  Icon, Orb, Eyebrow, Button, Card, CardHeader, CardBody, CardFooter, Stat, Chip, ChipGroup, Field,
  TrustPill, ThemeToggle, Scrim, Drawer, useDrawerContent, Modal,
  IconButton, Badge, Tabs, TabPanel, SegmentedControl, Checkbox, RadioGroup, Toggle, Slider,
  Alert, ErrorSummary, Toast, ToastProvider, useToast, EmptyState, LoadingState, DegradedState, ErrorState, SaveBar, ConfirmDialog,
  PageHeader, Section, SearchField, Popover, Menu, MenuItem, MenuSeparator, MenuLabel, Tooltip, FilterMenu, FilterBar, Pagination, LoadMore, DataTable, NavItem, NavRail, BottomBar,
  Meter, Avatar, KeyValue, Timeline, Stepper,
  FileField, ColorField, Thread, Message, MessageAttachments, ThreadNotice, Suggestions, AskComposer, SourceCard, SourceCardList, ProposalCard,
  Chart, Sparkline, Metric,
}
export * from './Collections';
export * from './Settings';
export * from './Flow';
export * from './Patterns';
export * from './Shell';
